import type {
  Agent,
  CreateAgentInput,
  UpdateAgentInput,
  AgentVersion,
  AgentAlias,
  Skill,
  CreateSkillInput,
  UpdateSkillInput,
  Tool,
  CreateToolInput,
  UpdateToolInput,
  GroupedToolsResponse,
  Email,
  CreateEmailInput,
  UpdateEmailInput,
  Schedule,
  CreateScheduleInput,
  UpdateScheduleInput,
  User,
  UserPreferences,
  SignInInput,
  SignUpInput,
  AuthResponse,
  InvokeInput,
  InvokeResponse,
  InvokeAsyncResponse,
  WsPresignTicket,
  DispatchActionInput,
  DispatchActionResponse,
  RunStatusResponse,
  ApiResponse,
  PaginatedResponse,
  Conversation,
  CreateConversationInput,
  UpdateConversationInput,
  ConversationWithMessages,
  PlatformCortex,
  ArtifactReference,
  ActivityItem,
  ActivityListParams,
  RetryRunResponse,
  OverviewResponse,
  OverviewIncludeKey,
  UpcomingSchedulesResponse,
  UpcomingSchedulesParams,
} from '../types';
import type {
  AuiNode,
  Canvas,
  Surface,
  CanvasLayout,
  MoveSurfacesInput,
  MoveSurfacesResponse,
} from '../types/canvases';
import type {
  Provider,
  Integration,
  IntegrationEvent,
  IntegrationEventDetail,
  IntegrationRedeliverResult,
  CreateIntegrationInput,
  UpdateIntegrationInput,
  RoutingRule,
  CreateRoutingRuleInput,
  RoutedEventType,
  Subscription,
} from '../types/integrations';
import type {
  Gateway,
  CreateGatewayInput,
  UpdateGatewayInput,
  GatewayTestResult,
  GatewayTool,
  GatewaySyncResult,
} from '../types/gateways';
import type {
  McpServer,
  CreateMcpServerInput,
  UpdateMcpServerInput,
  McpSyncResult,
  McpAuthStatus,
  OrgMcpServer,
  CreateOrgMcpServerInput,
  UpdateOrgMcpServerInput,
  OrgServiceAccountCredential,
  McpProvidersResponse,
} from '../types/mcp';
import type { Space, SpaceSummary } from '../types/spaces';
import type {
  InboxItem,
  InboxItemStatus,
  InboxItemWithSource,
  InboxRefType,
  AskUserAnswer,
  AskUserRequest,
  InboxCountsResponse,
  SpaceInboxCounts,
} from '../types/inbox';
import type { Label } from '../types/label';
import type {
  DotplotResponse,
  SummaryResponse,
  CatalogResponse,
} from '../types/analytics';

/** Server-supported `?expand=` values on the inbox endpoints. */
type InboxExpand = 'source' | 'labels';

function joinExpand(expand: InboxExpand | InboxExpand[]): string {
  return Array.isArray(expand) ? expand.join(',') : expand;
}
import { notifyServerVersion } from '@/lib/version-monitor';
import type {
  Plugin,
  InstallPluginInput,
  PluginInstallResult,
  PluginUpstream,
  SyncAckResponse,
  ExploreRequest,
  ExploreResponse,
  OrgPluginCatalogSource,
  OrgPluginCatalogSourcesResponse,
  OrgPluginCatalogResponse,
  OrgPluginCatalogEntry,
} from '../types/plugins';

// Use Vite's proxy in development, or direct URLs in production
const API_BASE = import.meta.env.VITE_API_URL || '';

let onAuthFailure: (() => void) | null = null;

export function setAuthFailureHandler(handler: (() => void) | null) {
  onAuthFailure = handler;
}

// Track when the last successful refresh happened (epoch ms).
// Used by auth-context to coordinate proactive refresh timing.
let lastRefreshTime = 0;

export function getLastRefreshTime(): number {
  return lastRefreshTime;
}

export function setLastRefreshTime(ts: number): void {
  lastRefreshTime = ts;
}

// Singleton refresh: concurrent 401s share a single in-flight refresh call.
let inflightRefresh: Promise<boolean> | null = null;

async function attemptRefresh(): Promise<boolean> {
  if (inflightRefresh) return inflightRefresh;

  inflightRefresh = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        lastRefreshTime = Date.now();
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      inflightRefresh = null;
    }
  })();

  return inflightRefresh;
}

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  // Every CloudFront response carries `x-app-version` (see frontend-stack.ts).
  // Feed it into the version monitor before any other branching so reload
  // prompts surface even when the body parse below throws.
  notifyServerVersion(response.headers.get('x-app-version'));

  // CloudFront rejects oversized cookie headers with a 431 and an HTML body —
  // calling .json() would throw a SyntaxError and erase the status signal.
  if (response.status === 431) {
    throw new ApiError(
      'Browser cookies for this site are too large.',
      431,
      'HEADERS_TOO_LARGE',
    );
  }

  type ErrorEnvelope = {
    error?: { message?: string; name?: string; code?: string } | string;
    message?: string;
    code?: string;
  };
  let data: T & ErrorEnvelope;
  try {
    data = (await response.json()) as T & ErrorEnvelope;
  } catch {
    throw new ApiError(
      response.statusText || 'Unexpected response',
      response.status,
      'PARSE_ERROR',
    );
  }

  if (!response.ok) {
    const err = data.error;
    const message =
      (err && typeof err === 'object' ? err.message : err) ||
      data.message ||
      'An error occurred';
    const code =
      (err && typeof err === 'object' ? err.code || err.name : undefined) || data.code;
    throw new ApiError(message, response.status, code);
  }

  return data;
}

async function fetchWithAuth(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  // Handle token expiration — attempt refresh on any 401
  if (response.status === 401) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      // Retry original request with fresh token
      return fetch(url, {
        ...options,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });
    }
    // Refresh failed — session is unrecoverable
    onAuthFailure?.();
  }

  return response;
}

// Auth API (now on same API Gateway as main API)
export const authApi = {
  async discover(
    email: string,
    /** Relative path to land on after a federated sign-in (deep-link preservation). */
    returnTo?: string,
  ): Promise<{ method: 'password' | 'okta'; authorizeUrl?: string }> {
    const response = await fetch(`${API_BASE}/api/v1/auth/discover`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, ...(returnTo ? { returnTo } : {}) }),
    });
    return handleResponse(response);
  },

  async signIn(input: SignInInput): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE}/api/v1/auth/signin`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    return handleResponse<AuthResponse>(response);
  },

  async signUp(input: SignUpInput): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE}/api/v1/auth/signup`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    return handleResponse<AuthResponse>(response);
  },

  async signOut(): Promise<void> {
    await fetch(`${API_BASE}/api/v1/auth/signout`, {
      method: 'POST',
      credentials: 'include',
    });
  },

  async refresh(): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    return handleResponse<AuthResponse>(response);
  },

  async me(): Promise<{ success: boolean; user: User }> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/auth/me?expand=preferences,extendedProfile`,
    );
    return handleResponse(response);
  },

  async updatePreferences(
    prefs: { [K in keyof UserPreferences]?: UserPreferences[K] | null },
  ): Promise<UserPreferences> {
    const response = await fetchWithAuth(`${API_BASE}/api/v1/auth/me/preferences`, {
      method: 'PATCH',
      body: JSON.stringify(prefs),
    });
    const data = await handleResponse<ApiResponse<{ preferences: UserPreferences }>>(response);
    return data.data!.preferences;
  },

  async updateProfile(
    fields: {
      firstName?: string;
      lastName?: string;
      timezone?: string;
      /** Activate an already-connected provider's picture (provider slug). */
      activeProfilePictureSource?: string;
    },
  ): Promise<{ success: boolean; updated: Partial<User> }> {
    const response = await fetchWithAuth(`${API_BASE}/api/v1/auth/me`, {
      method: 'PATCH',
      body: JSON.stringify(fields),
    });
    return handleResponse(response);
  },

  /**
   * Bypasses fetchWithAuth so a 401 INCORRECT_PASSWORD doesn't trigger refresh/sign-out
   * and a 403 FEDERATED_USER doesn't sign the user out — we surface those codes to the UI.
   */
  async changePassword(
    fields: { currentPassword: string; newPassword: string },
  ): Promise<{ success: true }> {
    const response = await fetch(`${API_BASE}/api/v1/auth/change-password`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
    });
    return handleResponse(response);
  },

  async getWsToken(): Promise<string> {
    const response = await fetchWithAuth(`${API_BASE}/api/v1/auth/ws-token`);
    const data = await handleResponse<{ token: string }>(response);
    return data.token;
  },
};

// ================================================================
// Device authorization flow (RFC 8628-shaped) — cross-device login.
// A shared/input-constrained browser (car, TV) starts the flow and polls;
// the user approves from their phone's signed-in session.
// ================================================================

export interface DeviceAuthorizeResponse {
  device_code: string;
  user_code: string;
  verification_uri: string;
  verification_uri_complete: string;
  expires_in: number;
  interval: number;
}

export type DeviceTokenResult =
  | { status: 'success'; user: User; expiresIn: number }
  | { status: 'pending' | 'slow_down' | 'expired' | 'denied' };

export interface DeviceRequestInfo {
  userCode: string;
  status: 'pending' | 'approved' | 'denied' | 'consumed';
  createdAt: string;
  expiresAt: number;
  client: { ip?: string; userAgent?: string };
}

export type DeviceApproveResult =
  | { status: 'approved' | 'denied' }
  /** Federated approver — finish via a (silent) Hosted-UI round-trip. */
  | { status: 'redirect'; authorizeUrl: string };

export const deviceApi = {
  async authorize(): Promise<DeviceAuthorizeResponse> {
    const response = await fetch(`${API_BASE}/api/v1/auth/device/authorize`, {
      method: 'POST',
      credentials: 'include',
    });
    return handleResponse(response);
  },

  /**
   * One poll tick. RFC 8628 protocol states arrive as 400s with an `error`
   * member — mapped to a result value rather than thrown, and deliberately
   * NOT routed through fetchWithAuth (there is no session to refresh yet).
   */
  async token(deviceCode: string): Promise<DeviceTokenResult> {
    const response = await fetch(`${API_BASE}/api/v1/auth/device/token`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ device_code: deviceCode }),
    });
    const data = (await response.json().catch(() => ({}))) as {
      success?: boolean;
      user?: User;
      expiresIn?: number;
      error?: string;
    };
    if (response.ok && data.success && data.user) {
      return { status: 'success', user: data.user, expiresIn: data.expiresIn ?? 3600 };
    }
    switch (data.error) {
      case 'authorization_pending':
        return { status: 'pending' };
      case 'slow_down':
        return { status: 'slow_down' };
      case 'access_denied':
        return { status: 'denied' };
      case 'expired_token':
        return { status: 'expired' };
      default:
        throw new ApiError(data.error || 'Device token poll failed', response.status);
    }
  },

  async info(userCode: string): Promise<DeviceRequestInfo> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/auth/device/info?user_code=${encodeURIComponent(userCode)}`,
    );
    return handleResponse(response);
  },

  async approve(userCode: string, action: 'approve' | 'deny'): Promise<DeviceApproveResult> {
    const response = await fetchWithAuth(`${API_BASE}/api/v1/auth/device/approve`, {
      method: 'POST',
      body: JSON.stringify({ user_code: userCode, action }),
    });
    return handleResponse(response);
  },
};

// ================================================================
// Backend response shapes — used by meApi/spaceApi to map snake-cased
// or platform-specific payloads (docs, auth tokens, search) into the
// typed shapes the rest of the app expects.
// ================================================================

// Platform Cortex API (global — outside the spaces hierarchy)
export const platformCortexApi = {
  async getLatest(): Promise<PlatformCortex> {
    const response = await fetchWithAuth(`${API_BASE}/api/v1/cortex/latest`);
    const data = await handleResponse<ApiResponse<PlatformCortex>>(response);
    return data.data!;
  },
};

// Models API
export const modelsApi = {
  async list(): Promise<import('../types').PlatformModel[]> {
    const response = await fetchWithAuth(`${API_BASE}/api/v1/models?limit=100`);
    const raw = await handleResponse<import('../types').ListModelsResponse>(response);
    return raw.data;
  },
};

// ================================================================
// VFS — unified file storage for every owner host.
//
// `ownerId` is the host's own ID (`con-…` | `skl-…`); the backend
// dispatches on prefix to look up the owning entity and reuse its
// existing ACL. One client, one event stream (`vfs.file.*`), one
// shape. Folder model is implicit — no mkdir, no empty folders.
// ================================================================

export const vfsApi = {
  async list(
    ownerId: string,
    params?: import('../types/vfs').VfsListParams,
  ): Promise<import('../types/vfs').VfsListResponse> {
    const qs = new URLSearchParams();
    if (params?.prefix) qs.set('prefix', params.prefix);
    if (params?.pattern) qs.set('pattern', params.pattern);
    if (params?.includeSystem != null) qs.set('includeSystem', String(params.includeSystem));
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.cursor) qs.set('cursor', params.cursor);
    const suffix = qs.toString() ? `?${qs}` : '';
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/vfs/${encodeURIComponent(ownerId)}${suffix}`,
    );
    // List endpoints return `{ data: [...], pagination }` — flatten to
    // the `{ items }` shape our hooks consume.
    const raw = await handleResponse<{
      data: import('../types/vfs').VfsEntry[];
      pagination?: { nextCursor?: string };
    }>(response);
    return { items: raw.data ?? [], nextCursor: raw.pagination?.nextCursor };
  },

  async initUpload(
    ownerId: string,
    input: { path: string; contentType?: string; ttlSeconds?: number },
  ): Promise<import('../types/vfs').VfsUploadInit> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/vfs/${encodeURIComponent(ownerId)}/init-upload`,
      { method: 'POST', body: JSON.stringify(input) },
    );
    return handleResponse<import('../types/vfs').VfsUploadInit>(response);
  },

  async completeUpload(
    ownerId: string,
    input: { path: string; fileId: string; contentType?: string },
  ): Promise<import('../types/vfs').VfsEntry> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/vfs/${encodeURIComponent(ownerId)}/complete-upload`,
      { method: 'POST', body: JSON.stringify(input) },
    );
    return handleResponse<import('../types/vfs').VfsEntry>(response);
  },

  async getDownloadUrl(
    ownerId: string,
    path: string,
  ): Promise<import('../types/vfs').VfsDownloadUrl> {
    const encodedPath = path.split('/').map(encodeURIComponent).join('/');
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/vfs/${encodeURIComponent(ownerId)}/${encodedPath}?download=1`,
    );
    return handleResponse<import('../types/vfs').VfsDownloadUrl>(response);
  },

  async delete(ownerId: string, path: string): Promise<void> {
    const encodedPath = path.split('/').map(encodeURIComponent).join('/');
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/vfs/${encodeURIComponent(ownerId)}/${encodedPath}`,
      { method: 'DELETE' },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new ApiError(body.error || body.message || 'Delete failed', response.status, body.code);
    }
  },

  /** Recursive folder delete. Removes every file whose path starts with
   *  `${path}/`. Always recursive — there's no opt-out flag. Backend emits
   *  `vfs.file.*` WebSocket events that `useVfsFiles` already consumes, so
   *  the tree drains without extra client handling.
   *
   *  POST (not DELETE) deliberately — DELETE on the same path shape would
   *  collide with the single-file delete route. */
  async deleteFolder(
    ownerId: string,
    path: string,
  ): Promise<{ ownerId: string; path: string; deletedCount: number }> {
    const encodedPath = path.split('/').map(encodeURIComponent).join('/');
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/vfs/${encodeURIComponent(ownerId)}/delete-folder/${encodedPath}`,
      { method: 'POST' },
    );
    return handleResponse<{ ownerId: string; path: string; deletedCount: number }>(response);
  },

  /** Rename or move a file within the same owner. Backend is atomic and
   *  emits a single `vfs.file.moved` event. Pass `overwrite: true` to
   *  permit clobbering an existing destination (otherwise the call fails
   *  with HTTP 409 when `to` already exists). */
  async move(
    ownerId: string,
    input: { from: string; to: string; overwrite?: boolean },
  ): Promise<import('../types/vfs').VfsEntry> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/vfs/${encodeURIComponent(ownerId)}/move`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      },
    );
    return handleResponse<import('../types/vfs').VfsEntry>(response);
  },

  /** Import a conversation VFS file into a knowledge base. `ownerId` is the
   *  conversation id (`con-…`); the target KB is named by `input.kbId`. Bytes
   *  move server-side (binary-safe, 5 GB cap) — no client upload. The KB
   *  auto-indexes/OCRs the file after import and emits `kb.vfs.file.created`.
   *  `mode: 'move'` deletes the source afterwards. */
  async copyToKb(
    ownerId: string,
    input: import('../types/vfs').VfsCopyToKbInput,
  ): Promise<{ kbPath: string }> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/vfs/${encodeURIComponent(ownerId)}/copy-to-kb`,
      { method: 'POST', body: JSON.stringify(input) },
    );
    return handleResponse<{ kbPath: string }>(response);
  },

  /** Export a KB file into a conversation VFS. `ownerId` is the conversation
   *  id (`con-…`); the source KB is named by `input.kbId`. Returns the new
   *  conversation `VfsEntry` and emits `vfs.file.created`. `mode: 'move'`
   *  deletes the KB source afterwards (requires KB write). */
  async copyFromKb(
    ownerId: string,
    input: import('../types/vfs').VfsCopyFromKbInput,
  ): Promise<import('../types/vfs').VfsEntry> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/vfs/${encodeURIComponent(ownerId)}/copy-from-kb`,
      { method: 'POST', body: JSON.stringify(input) },
    );
    return handleResponse<import('../types/vfs').VfsEntry>(response);
  },
};

// ================================================================
// Skill files — `/api/v1/skills/{skillId}/files`
//
// Dedicated per-skill file storage (markdown instructions, SQL,
// scripts, …). Org-scoped by `skillId` alone — no space prefix. Unlike
// the unified VFS this uses inline base64 upload (no presigned PUT),
// returns a leaner `SkillFileRef` (no `fileId` — addressed by `path`),
// and emits no WebSocket events (the adapter keeps an optimistic store
// instead). Folder refs carry `contentType: "inode/directory"`.
//
// `{filepath+}` is a greedy path — encode each segment and join with
// real slashes (same rule as `vfsApi`).
// ================================================================

export const skillFilesApi = {
  async list(skillId: string): Promise<import('../types/skill-files').SkillFileRef[]> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/skills/${encodeURIComponent(skillId)}/files`,
    );
    const raw = await handleResponse<{
      data: import('../types/skill-files').SkillFileRef[];
    }>(response);
    return raw.data ?? [];
  },

  /** Create or replace the file at `path` (same path overwrites). `data`
   *  is the file body base64-encoded. Caps: 10 MB/file, 100 MB/skill. */
  async upload(
    skillId: string,
    input: { path: string; contentType: string; data: string },
  ): Promise<import('../types/skill-files').SkillFileRef> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/skills/${encodeURIComponent(skillId)}/files`,
      { method: 'POST', body: JSON.stringify(input) },
    );
    const raw = await handleResponse<{
      data: import('../types/skill-files').SkillFileRef;
    }>(response);
    return raw.data;
  },

  /** Presigned download URL for `path` (NOT the bytes). Follow `url`
   *  directly to fetch the content — no cookie needed for S3. */
  async getDownloadUrl(
    skillId: string,
    path: string,
  ): Promise<{ url: string; filepath: string }> {
    const encodedPath = path.split('/').map(encodeURIComponent).join('/');
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/skills/${encodeURIComponent(skillId)}/files/${encodedPath}`,
    );
    const raw = await handleResponse<{
      data: { url: string; filepath: string };
    }>(response);
    return raw.data;
  },

  /** Delete a file (exact path) or a folder (prefix, cascade). Idempotent —
   *  `deletedCount: 0` means nothing matched. */
  async delete(skillId: string, path: string): Promise<{ deletedCount: number }> {
    const encodedPath = path.split('/').map(encodeURIComponent).join('/');
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/skills/${encodeURIComponent(skillId)}/files/${encodedPath}`,
      { method: 'DELETE' },
    );
    const raw = await handleResponse<{
      data: { deletedCount: number };
    }>(response);
    return raw.data;
  },

  /** Move/rename a file or folder. Returns HTTP 409 when `to` exists and
   *  `overwrite !== true` — callers surface that to the user. */
  async move(
    skillId: string,
    input: { from: string; to: string; overwrite?: boolean },
  ): Promise<import('../types/skill-files').SkillFileRef> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/skills/${encodeURIComponent(skillId)}/files/move`,
      { method: 'POST', body: JSON.stringify(input) },
    );
    const raw = await handleResponse<{
      data: import('../types/skill-files').SkillFileRef;
    }>(response);
    return raw.data;
  },
};

// ================================================================
// Knowledge Bases (KB) — `/api/v1/kb/...`
//
// Multi-KB-per-space file system served by the `optr-api-kb` HTTP API.
// Flat URL space — the backend resolves caller identity + active space
// from the cookie-based authorizer.
//
// {path+} segments are encoded as ONE whole URL segment
// (e.g. `docs/report.pdf` → `docs%2Freport.pdf`), per the backend
// spec. The internal `encodePath` helper centralises that rule.
// ================================================================

const encodePath = (path: string) => encodeURIComponent(path);

/**
 * The KB API wraps single-object responses in `{ success, data }`.
 * Tolerate both the wrapped and unwrapped shapes — same pragmatic
 * envelope-stripping approach used by `meApi.inboxItem`.
 */
function unwrapKbEnvelope<T>(raw: unknown): T {
  if (
    raw &&
    typeof raw === 'object' &&
    'data' in (raw as Record<string, unknown>)
  ) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

/**
 * The move/copy endpoints double-wrap: `{ success, data: { node: <Node> } }`.
 * Other single-object endpoints land at `{ success, data: <object> }`.
 * Walk both layers — if the inner payload is `{ node }` and nothing
 * else, peel that too.
 */
function unwrapKbNode<T>(raw: unknown): T {
  const inner = unwrapKbEnvelope<unknown>(raw);
  if (inner && typeof inner === 'object') {
    const obj = inner as Record<string, unknown>;
    if ('node' in obj && obj.node && typeof obj.node === 'object') {
      return obj.node as T;
    }
  }
  return inner as T;
}

export const kbApi = {
  // -- Knowledge bases --------------------------------------------------

  async list(params?: {
    scope?: import('../types/kb').KbScope;
    limit?: number;
    cursor?: string;
  }): Promise<PaginatedResponse<import('../types/kb').KnowledgeBase>> {
    const qs = new URLSearchParams();
    if (params?.scope) qs.set('scope', params.scope);
    qs.set('limit', String(params?.limit ?? 50));
    if (params?.cursor) qs.set('cursor', params.cursor);
    const response = await fetchWithAuth(`${API_BASE}/api/v1/kb?${qs.toString()}`);
    const raw = await handleResponse<
      import('../types/kb').KbPaginated<import('../types/kb').KnowledgeBase>
    >(response);
    return {
      items: raw.data ?? [],
      nextToken: raw.pagination?.nextCursor ?? undefined,
    };
  },

  async create(
    input: import('../types/kb').CreateKnowledgeBaseInput,
  ): Promise<import('../types/kb').KnowledgeBase> {
    const response = await fetchWithAuth(`${API_BASE}/api/v1/kb`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const raw = await handleResponse<unknown>(response);
    return unwrapKbEnvelope<import('../types/kb').KnowledgeBase>(raw);
  },

  async get(kbId: string): Promise<import('../types/kb').KnowledgeBase> {
    const response = await fetchWithAuth(`${API_BASE}/api/v1/kb/${encodeURIComponent(kbId)}`);
    const raw = await handleResponse<unknown>(response);
    return unwrapKbEnvelope<import('../types/kb').KnowledgeBase>(raw);
  },

  async remove(kbId: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE}/api/v1/kb/${encodeURIComponent(kbId)}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new ApiError(body.error || body.message || 'Delete failed', response.status, body.code);
    }
  },

  // -- Files & folders --------------------------------------------------

  async listNodes(
    kbId: string,
    params?: { path?: string; recursive?: boolean; limit?: number; cursor?: string },
  ): Promise<PaginatedResponse<import('../types/kb').VfsNode>> {
    const qs = new URLSearchParams();
    if (params?.path) qs.set('path', params.path);
    if (params?.recursive) qs.set('recursive', '1');
    qs.set('limit', String(params?.limit ?? 50));
    if (params?.cursor) qs.set('cursor', params.cursor);
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/kb/${encodeURIComponent(kbId)}/nodes?${qs.toString()}`,
    );
    const raw = await handleResponse<
      import('../types/kb').KbPaginated<import('../types/kb').VfsNode>
    >(response);
    return {
      items: raw.data ?? [],
      nextToken: raw.pagination?.nextCursor ?? undefined,
    };
  },

  async statNode(kbId: string, path: string): Promise<import('../types/kb').VfsNode> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/kb/${encodeURIComponent(kbId)}/nodes/${encodePath(path)}`,
    );
    const raw = await handleResponse<unknown>(response);
    return unwrapKbNode<import('../types/kb').VfsNode>(raw);
  },

  /** Resolve a file's stable id (`doc-…`) to its live node (including the
   *  current `path`, which follows moves/renames). Backs deep links of the
   *  form `/knowledge/:kbId/f/:fileId`, where only the id is in the URL and
   *  the browser needs the path to reveal + open the file. The gateway
   *  collapses every "not resolvable" case (unknown / moved-out / folder id /
   *  cross-KB) to a 404 — a first-class "file no longer here" state, not an
   *  error. Envelope is `{ success, data: <node> }` (unwrapped below). */
  async statNodeById(
    kbId: string,
    fileId: string,
  ): Promise<import('../types/kb').VfsNode> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/kb/${encodeURIComponent(kbId)}/files/${encodeURIComponent(fileId)}`,
    );
    const raw = await handleResponse<unknown>(response);
    return unwrapKbNode<import('../types/kb').VfsNode>(raw);
  },

  /** GET /content — returns the file body as text.
   *
   *  Backend wraps text content in `{ success, data: { node, content } }`
   *  (JSON, not raw bytes); unwrap to the markdown / text string. Falls
   *  back to the raw body if the response isn't JSON (defensive — if a
   *  future deploy switches to streaming raw content).
   *
   *  Pass `derivative` to read a derived view of the file (e.g.
   *  `'ocr'` for the OCR text of an image / PDF). The endpoint takes
   *  the original file's `path`, not the derivative's s3Key. Missing
   *  derivatives surface as 404. */
  async readText(kbId: string, path: string, derivative?: string): Promise<string> {
    const qs = derivative ? `?derivative=${encodeURIComponent(derivative)}` : '';
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/kb/${encodeURIComponent(kbId)}/content/${encodePath(path)}${qs}`,
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new ApiError(body.error || body.message || 'Read failed', response.status, body.code);
    }
    const ct = (response.headers.get('content-type') ?? '').toLowerCase();
    if (!ct.includes('application/json')) {
      return response.text();
    }
    const json = (await response.json()) as unknown;
    const payload = unwrapKbEnvelope<{ content?: string } | string | null>(json);
    if (typeof payload === 'string') return payload;
    if (payload && typeof payload === 'object' && typeof payload.content === 'string') {
      return payload.content;
    }
    return '';
  },

  /** GET /download/{path+} → presigned URL string (or wrapped).
   *
   *  Pass `derivative` to fetch a presigned URL for a binary derived
   *  artefact (e.g. `'thumbnail'`). Same rule as `readText`: the
   *  endpoint takes the original file's `path`, not the derivative's
   *  s3Key, and missing derivatives surface as 404. */
  async downloadUrl(kbId: string, path: string, derivative?: string): Promise<string> {
    const qs = derivative ? `?derivative=${encodeURIComponent(derivative)}` : '';
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/kb/${encodeURIComponent(kbId)}/download/${encodePath(path)}${qs}`,
    );
    const raw = await handleResponse<unknown>(response);
    if (typeof raw === 'string') return raw;
    const data = unwrapKbEnvelope<
      { downloadUrl?: string; url?: string } | string
    >(raw);
    if (typeof data === 'string') return data;
    return data.downloadUrl ?? data.url ?? '';
  },

  /** Small inline write — for create/replace text files only. Large
   *  uploads go through `initUpload` → presigned PUT → `completeUpload`. */
  async writeInline(
    kbId: string,
    path: string,
    body: { content: string; contentType?: string; overwrite?: boolean },
  ): Promise<import('../types/kb').VfsNode> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/kb/${encodeURIComponent(kbId)}/content/${encodePath(path)}`,
      { method: 'PUT', body: JSON.stringify(body) },
    );
    const raw = await handleResponse<unknown>(response);
    return unwrapKbNode<import('../types/kb').VfsNode>(raw);
  },

  async initUpload(
    kbId: string,
    input: { path: string; contentType?: string },
  ): Promise<{
    fileId: string;
    uploadUrl: string;
    uploadHeaders?: Record<string, string>;
    expiresAt?: string;
  }> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/kb/${encodeURIComponent(kbId)}/uploads`,
      { method: 'POST', body: JSON.stringify(input) },
    );
    // Backend wraps the payload in `{ success, data }` and nests the
    // presigned PUT under `data.upload`. Tolerate both the wrapped form
    // and a few historical flat shapes we've seen during the migration.
    const raw = await handleResponse<{
      success?: boolean;
      // Wrapped (current backend): `{ success, data: { fileId, upload: { url, headers } } }`
      data?: {
        fileId: string;
        upload?: { url?: string; headers?: Record<string, string> };
        url?: string;
        uploadUrl?: string;
        headers?: Record<string, string>;
        uploadHeaders?: Record<string, string>;
        expiresAt?: string;
      };
      // Flat fallbacks
      fileId?: string;
      url?: string;
      uploadUrl?: string;
      upload?: { url?: string; headers?: Record<string, string> };
      headers?: Record<string, string>;
      uploadHeaders?: Record<string, string>;
      expiresAt?: string;
    }>(response);

    const payload = raw.data ?? raw;
    const fileId = payload.fileId ?? '';
    const uploadUrl =
      payload.upload?.url ?? payload.uploadUrl ?? payload.url ?? '';
    const uploadHeaders =
      payload.upload?.headers ?? payload.uploadHeaders ?? payload.headers;

    return {
      fileId,
      uploadUrl,
      uploadHeaders,
      expiresAt: payload.expiresAt,
    };
  },

  async completeUpload(
    kbId: string,
    input: { path: string; fileId: string; contentType?: string; sizeBytes?: number },
  ): Promise<import('../types/kb').VfsNode> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/kb/${encodeURIComponent(kbId)}/uploads/complete`,
      { method: 'POST', body: JSON.stringify(input) },
    );
    const raw = await handleResponse<unknown>(response);
    return unwrapKbNode<import('../types/kb').VfsNode>(raw);
  },

  async mkdir(kbId: string, input: { path: string }): Promise<import('../types/kb').VfsNode> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/kb/${encodeURIComponent(kbId)}/folders`,
      { method: 'POST', body: JSON.stringify(input) },
    );
    const raw = await handleResponse<unknown>(response);
    return unwrapKbNode<import('../types/kb').VfsNode>(raw);
  },

  async move(
    kbId: string,
    input: { from: string; to: string; overwrite?: boolean },
  ): Promise<import('../types/kb').VfsNode> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/kb/${encodeURIComponent(kbId)}/nodes/move`,
      { method: 'POST', body: JSON.stringify(input) },
    );
    const raw = await handleResponse<unknown>(response);
    return unwrapKbNode<import('../types/kb').VfsNode>(raw);
  },

  async copy(
    kbId: string,
    input: { from: string; to: string; overwrite?: boolean },
  ): Promise<import('../types/kb').VfsNode> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/kb/${encodeURIComponent(kbId)}/nodes/copy`,
      { method: 'POST', body: JSON.stringify(input) },
    );
    const raw = await handleResponse<unknown>(response);
    return unwrapKbNode<import('../types/kb').VfsNode>(raw);
  },

  async removeNode(kbId: string, path: string): Promise<void> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/kb/${encodeURIComponent(kbId)}/nodes/${encodePath(path)}`,
      { method: 'DELETE' },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new ApiError(body.error || body.message || 'Delete failed', response.status, body.code);
    }
  },

  // -- Search -----------------------------------------------------------

  async search(
    kbId: string,
    input: { query: string; topK?: number; includeText?: boolean },
  ): Promise<import('../types/kb').KbSearchResponse> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/kb/${encodeURIComponent(kbId)}/search`,
      { method: 'POST', body: JSON.stringify(input) },
    );
    const raw = await handleResponse<unknown>(response);
    return unwrapKbEnvelope<import('../types/kb').KbSearchResponse>(raw);
  },

  /** Cross-KB fan-out across the caller's accessible KBs. */
  async searchSpace(input: {
    scope?: import('../types/kb').KbScope;
    spaceId?: string;
    query: string;
    topK?: number;
    includeText?: boolean;
  }): Promise<import('../types/kb').KbSearchResponse> {
    const response = await fetchWithAuth(`${API_BASE}/api/v1/kb/search`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const raw = await handleResponse<unknown>(response);
    return unwrapKbEnvelope<import('../types/kb').KbSearchResponse>(raw);
  },
};

// ================================================================
// Spaces — Phase 1
//
// Cross-space (`/api/v1/me/...`) and space-scoped (`/api/v1/spaces/{id}/...`)
// endpoints. The legacy flat namespaces above remain in place until every
// caller has migrated; they're removed in the final cleanup pass.
// ================================================================

/**
 * Cross-space and user-scoped endpoints. Backed by `/api/v1/me/...` on the
 * backend; aggregates across every space the user belongs to.
 */
export const meApi = {
  async me(): Promise<{ user: User; defaultSpaceId: string }> {
    const response = await fetchWithAuth(`${API_BASE}/api/v1/me`);
    return handleResponse(response);
  },

  async spaces(opts?: { includeArchived?: boolean }): Promise<PaginatedResponse<SpaceSummary>> {
    const qs = opts?.includeArchived ? '?includeArchived=true' : '';
    const response = await fetchWithAuth(`${API_BASE}/api/v1/me/spaces${qs}`);
    // Backend shape: each row is { space: Space, role: SpaceRole } — flatten
    // into the SpaceSummary the switcher and SpaceContext consume.
    const raw = await handleResponse<{
      data: Array<{ space: Space; role: import('../types/spaces').SpaceRole }>;
      pagination?: unknown;
    }>(response);
    return {
      items: (raw.data ?? []).map(({ space, role }) => ({
        spaceId: space.spaceId,
        name: space.name,
        kind: space.kind,
        role,
        status: space.status ?? 'active',
      })),
    };
  },

  async overview(include?: OverviewIncludeKey[]): Promise<OverviewResponse> {
    const qs = include && include.length ? `?include=${include.join(',')}` : '';
    const response = await fetchWithAuth(`${API_BASE}/api/v1/me/overview${qs}`);
    return handleResponse<OverviewResponse>(response);
  },

  async activities(params?: ActivityListParams): Promise<{
    items: ActivityItem[];
    nextToken?: string;
  }> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.cursor) searchParams.set('cursor', params.cursor);
    if (params?.scope) searchParams.set('scope', params.scope);
    if (params?.source) searchParams.set('source', params.source);
    if (params?.priority) searchParams.set('priority', params.priority);
    if (params?.category) searchParams.set('category', params.category);
    if (params?.expand?.length) searchParams.set('expand', params.expand.join(','));
    const qs = searchParams.toString();
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/me/activities${qs ? `?${qs}` : ''}`,
    );
    const raw = await handleResponse<{
      data: ActivityItem[];
      pagination: { count: number; nextCursor: string | null; hasMore: boolean };
    }>(response);
    return {
      items: raw.data ?? [],
      nextToken: raw.pagination?.nextCursor ?? undefined,
    };
  },

  async runs(params?: import('../types/runs').RunListParams): Promise<{
    items: RunStatusResponse[];
    nextToken?: string;
  }> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.cursor) searchParams.set('cursor', params.cursor);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.agentId) searchParams.set('agentId', params.agentId);
    const qs = searchParams.toString();
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/me/runs${qs ? `?${qs}` : ''}`,
    );
    const raw = await handleResponse<{
      data: RunStatusResponse[];
      pagination: { count: number; nextCursor: string | null; hasMore: boolean };
    }>(response);
    return {
      items: raw.data ?? [],
      nextToken: raw.pagination?.nextCursor ?? undefined,
    };
  },

  /**
   * Cross-space knowledge-base aggregator. Returns every KB the user can
   * see across every space they belong to (plus accessible org KBs).
   * Use this for the operator surface; space-scoped consoles call
   * `kbApi.list()` instead (current-space view).
   */
  async kb(opts?: { limit?: number; cursor?: string }): Promise<
    PaginatedResponse<import('../types/kb').KnowledgeBase>
  > {
    const qs = new URLSearchParams();
    qs.set('limit', String(opts?.limit ?? 100));
    if (opts?.cursor) qs.set('cursor', opts.cursor);
    const response = await fetchWithAuth(`${API_BASE}/api/v1/me/kb?${qs.toString()}`);
    const raw = await handleResponse<
      import('../types/kb').KbPaginated<import('../types/kb').KnowledgeBase>
    >(response);
    return {
      items: raw.data ?? [],
      nextToken: raw.pagination?.nextCursor ?? undefined,
    };
  },

  async conversations(opts?: {
    limit?: number;
    cursor?: string;
    persistent?: boolean | 'all';
    origin?: 'user' | 'background' | 'all';
  }): Promise<PaginatedResponse<Conversation> & { pinned: Conversation[] }> {
    const search = new URLSearchParams();
    search.set('limit', String(opts?.limit ?? 30));
    if (opts?.cursor) search.set('cursor', opts.cursor);
    if (opts?.persistent !== undefined) search.set('persistent', String(opts.persistent));
    if (opts?.origin) search.set('origin', opts.origin);
    const response = await fetchWithAuth(`${API_BASE}/api/v1/me/conversations?${search.toString()}`);
    const raw = await handleResponse<{
      data: Conversation[];
      pinned?: Conversation[];
      pagination: { count: number; nextCursor: string | null; hasMore: boolean };
    }>(response);
    return {
      items: raw.data ?? [],
      // Complete, unpaginated set of pinned conversations. Only populated on
      // the first page (no cursor); paginated follow-ups return [].
      pinned: raw.pinned ?? [],
      nextToken: raw.pagination?.nextCursor ?? undefined,
    };
  },

  // ----------------------------------------------------------------
  // Personal inbox (HITL ask-user requests).
  // ----------------------------------------------------------------

  /**
   * Cross-space label aggregator — one call returns every label in every
   * space the signed-in user is an active member of. Backend keeps this
   * single-page (label sets are small), so no pagination loop needed.
   *
   * Always passes `?includeArchived=true` so a single cache feeds both the
   * inbox rail/picker (filters archived out client-side) and the per-space
   * Labels management tab (needs to render archived rows with Unarchive).
   * Without it, archived labels still attached to historical inbox items
   * would silently fail to hydrate in `LabelChips`.
   */
  async labels(): Promise<Label[]> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/me/labels?includeArchived=true`,
    );
    const raw = await handleResponse<{
      data: Label[];
      pagination?: { count?: number; nextCursor?: string | null; hasMore?: boolean };
    }>(response);
    return raw.data ?? [];
  },

  async inbox(opts?: {
    status?: InboxItemStatus | 'all';
    refType?: InboxRefType;
    limit?: number;
    cursor?: string;
    /** Comma-joined when an array — e.g. ['labels'] → `?expand=labels`. */
    expand?: InboxExpand | InboxExpand[];
  }): Promise<PaginatedResponse<InboxItem>> {
    const search = new URLSearchParams();
    search.set('status', opts?.status ?? 'pending');
    if (opts?.refType) search.set('refType', opts.refType);
    search.set('limit', String(opts?.limit ?? 30));
    if (opts?.cursor) search.set('cursor', opts.cursor);
    if (opts?.expand) {
      search.set('expand', joinExpand(opts.expand));
    }
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/me/inbox?${search.toString()}`,
    );
    const raw = await handleResponse<{
      data: InboxItem[];
      pagination?: { count?: number; nextCursor?: string | null; hasMore?: boolean };
    }>(response);
    return {
      items: raw.data ?? [],
      nextToken: raw.pagination?.nextCursor ?? undefined,
    };
  },

  async inboxItem(
    inboxItemId: string,
    opts?: { expand?: InboxExpand | InboxExpand[] },
  ): Promise<InboxItemWithSource> {
    const qs = opts?.expand ? `?expand=${joinExpand(opts.expand)}` : '';
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/me/inbox/${inboxItemId}${qs}`,
    );
    // Backend shape: `{ success, data: InboxItem, expanded: { askUserRequest } }`.
    // The expanded source lives under `expanded.askUserRequest`, not `data.source`.
    const raw = await handleResponse<{
      success?: boolean;
      data: InboxItem;
      expanded?: { askUserRequest?: AskUserRequest };
    }>(response);
    const item = raw.data;
    const source = raw.expanded?.askUserRequest;
    return source ? { ...item, source } : item;
  },

  /**
   * Maintained per-space pending counts (Gmail-style sidebar badges) for
   * every space the caller belongs to. Returns raw JSON — no
   * `{ success, data }` envelope. Personal items aren't counted.
   */
  async inboxCounts(): Promise<InboxCountsResponse> {
    const response = await fetchWithAuth(`${API_BASE}/api/v1/me/inbox/counts`);
    return handleResponse<InboxCountsResponse>(response);
  },

  // Cross-space entity lookups — used by operator-side surfaces (Builder
  // canvas, RecentActivitiesPanel, etc.) that need to reach across spaces.
  // Each row carries `spaceId` + `spaceName` for client-side grouping.

  async agents(): Promise<PaginatedResponse<Agent>> {
    const response = await fetchWithAuth(`${API_BASE}/api/v1/me/agents?limit=500`);
    const raw = await handleResponse<{ data: Agent[]; pagination: unknown }>(response);
    return { items: raw.data };
  },

  async skills(): Promise<PaginatedResponse<Skill>> {
    const response = await fetchWithAuth(`${API_BASE}/api/v1/me/skills?limit=500`);
    const raw = await handleResponse<{ data: Skill[]; pagination: unknown }>(response);
    return { items: raw.data };
  },

  async tools(): Promise<PaginatedResponse<Tool>> {
    const response = await fetchWithAuth(`${API_BASE}/api/v1/me/tools?limit=500`);
    const raw = await handleResponse<{ data: Tool[]; pagination: unknown }>(response);
    return { items: raw.data };
  },

  // ----------------------------------------------------------------
  // Org-level / cross-space management endpoints. Single-entity reads
  // for spaces/runs/etc. still live on spaceApi(spaceId).
  // ----------------------------------------------------------------

  /** Create a new Shared Space. Caller becomes Owner. POST /spaces returns
   *  raw `{ space }` (no `success` wrapper) — handle accordingly. */
  async createSpace(input: { name: string; description?: string }): Promise<Space> {
    const response = await fetchWithAuth(`${API_BASE}/api/v1/spaces`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const raw = await handleResponse<{ space: Space }>(response);
    return raw.space;
  },

  /** Org user directory — powers the Add-Member picker. Org-scoped via
   *  cookie auth; can't see users from other orgs. */
  async users(params?: {
    q?: string;
    limit?: number;
    cursor?: string;
  }): Promise<{ items: import('../types/spaces').UserSummary[]; nextToken?: string }> {
    const sp = new URLSearchParams();
    if (params?.q) sp.set('q', params.q);
    if (params?.limit) sp.set('limit', String(params.limit));
    if (params?.cursor) sp.set('cursor', params.cursor);
    const qs = sp.toString();
    const response = await fetchWithAuth(`${API_BASE}/api/v1/users${qs ? `?${qs}` : ''}`);
    const raw = await handleResponse<{
      data: import('../types/spaces').UserSummary[];
      pagination: { count: number; nextCursor?: string; hasMore: boolean };
    }>(response);
    return {
      items: raw.data ?? [],
      nextToken: raw.pagination?.nextCursor,
    };
  },
};

/**
 * Space-scoped ask-user-request actions backing the HITL inbox.
 * Answer/cancel routes live under `/spaces/{spaceId}/...`; an inbox item with
 * `spaceId == null` is therefore non-actionable — surface that in the UI.
 *
 * `answer` returns the latest AskUserRequest on success and on 409
 * ("already answered") so the UI converges to the server state without a
 * second round-trip.
 */
export const askUserApi = {
  async answer(
    spaceId: string,
    requestId: string,
    answers: AskUserAnswer[],
    opts?: {
      /**
       * Resolve the request + inbox item WITHOUT dispatching a follow-up run
       * (the turn already continued over the live socket). Used by the
       * post-answer verifier as a fallback when the WS closeout failed.
       */
      continueOverWs?: boolean;
    },
  ): Promise<AskUserRequest> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/spaces/${spaceId}/ask-user-requests/${requestId}/answer`,
      {
        method: 'POST',
        body: JSON.stringify({
          answers,
          ...(opts?.continueOverWs && { continueOverWs: true }),
        }),
      },
    );
    const raw = await handleResponse<{ data: AskUserRequest } | AskUserRequest>(response);
    return (raw as { data?: AskUserRequest }).data ?? (raw as AskUserRequest);
  },

  async cancel(
    spaceId: string,
    requestId: string,
    reason?: string,
  ): Promise<AskUserRequest> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/spaces/${spaceId}/ask-user-requests/${requestId}/cancel`,
      { method: 'POST', body: JSON.stringify(reason ? { reason } : {}) },
    );
    const raw = await handleResponse<{ data: AskUserRequest } | AskUserRequest>(response);
    return (raw as { data?: AskUserRequest }).data ?? (raw as AskUserRequest);
  },
};

/**
 * Space-scoped inbox endpoints. The cross-space `/me/inbox` lives on
 * `meApi`; these team-queue calls accept a `label` filter (slug or id)
 * and the relabel mutation.
 */
export const spaceInboxApi = {
  async list(opts: {
    spaceId: string;
    /** Slug or labelId; backend accepts either. */
    label?: string;
    status?: InboxItemStatus | 'all';
    refType?: InboxRefType;
    limit?: number;
    cursor?: string;
    expand?: InboxExpand | InboxExpand[];
  }): Promise<PaginatedResponse<InboxItem>> {
    const search = new URLSearchParams();
    search.set('status', opts.status ?? 'pending');
    if (opts.refType) search.set('refType', opts.refType);
    if (opts.label) search.set('label', opts.label);
    search.set('limit', String(opts.limit ?? 30));
    if (opts.cursor) search.set('cursor', opts.cursor);
    if (opts.expand) search.set('expand', joinExpand(opts.expand));
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/spaces/${opts.spaceId}/inbox?${search.toString()}`,
    );
    const raw = await handleResponse<{
      data: InboxItem[];
      pagination?: { count?: number; nextCursor?: string | null; hasMore?: boolean };
    }>(response);
    return {
      items: raw.data ?? [],
      nextToken: raw.pagination?.nextCursor ?? undefined,
    };
  },

  /** Atomic add/remove of labels on a single inbox item. Emits
   *  `inbox.item_updated` server-side. */
  async relabel(
    spaceId: string,
    inboxItemId: string,
    body: { add?: string[]; remove?: string[] },
  ): Promise<InboxItem> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/spaces/${spaceId}/inbox/${inboxItemId}/labels`,
      { method: 'POST', body: JSON.stringify(body) },
    );
    const raw = await handleResponse<{ data: InboxItem } | InboxItem>(response);
    return (raw as { data?: InboxItem }).data ?? (raw as InboxItem);
  },

  /** Pending counts for a single space. Returns raw JSON. */
  async counts(spaceId: string): Promise<SpaceInboxCounts> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/spaces/${spaceId}/inbox/counts`,
    );
    return handleResponse<SpaceInboxCounts>(response);
  },

  /** Force a fresh re-derivation from source-of-truth rows; use only when
   *  the cache looks wrong (support/admin affordance). Returns the freshly
   *  recomputed counts. */
  async recount(spaceId: string): Promise<SpaceInboxCounts> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/spaces/${spaceId}/inbox/counts/recount`,
      { method: 'POST' },
    );
    return handleResponse<SpaceInboxCounts>(response);
  },
};

/**
 * Space-scoped label CRUD. Backend rejects unknown slugs on relabel, so
 * `create` is the only path to introduce a new label. No delete endpoint.
 */
export const labelsApi = {
  async create(
    spaceId: string,
    body: { name: string; slug?: string; color?: string; description?: string },
  ): Promise<Label> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/spaces/${spaceId}/labels`,
      { method: 'POST', body: JSON.stringify(body) },
    );
    const raw = await handleResponse<{ data: Label } | Label>(response);
    return (raw as { data?: Label }).data ?? (raw as Label);
  },

  async update(
    spaceId: string,
    labelId: string,
    patch: {
      name?: string;
      color?: string;
      description?: string;
      /** `true` archives, `false` unarchives. May be combined with rename/recolor. */
      archived?: boolean;
    },
  ): Promise<Label> {
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/spaces/${spaceId}/labels/${labelId}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    );
    const raw = await handleResponse<{ data: Label } | Label>(response);
    return (raw as { data?: Label }).data ?? (raw as Label);
  },
};

/**
 * Identity-team credential APIs (`/api/v1/identity/...`,
 * `/spaces/{id}/credentials`, `/org/credentials`, `/credentials/{credId}/*`).
 *
 * Authorization is enforced server-side on every route:
 *   - Space: space-admin (or org-admin override) for writes; any member for reads.
 *   - Org: org-admin only — non-admins get 403/404 on `/org/credentials`.
 *
 * Failure-as-data: `/credentials/{credId}/verify` returns HTTP 200 with
 * `{ ok: false, reason }` for bad credentials — don't treat it as throw.
 */
export const identityApi = {
  /** Canonical provider catalog — org-agnostic, safe to cache aggressively. */
  /**
   * The trust policy an AWS account owner attaches to a role so OPTR can
   * assume it (ExternalId + `optr-*` session-tag conditions). Rendered
   * server-side; `spaceId` / `agentIds` add space / agent conditions.
   */
  async awsRoleTrustPolicy(opts: {
    spaceId?: string;
    agentIds?: string[];
  }): Promise<import('../types/identity').AwsRoleTrustPolicy> {
    const params = new URLSearchParams();
    if (opts.spaceId) params.set('spaceId', opts.spaceId);
    if (opts.agentIds?.length) params.set('agentIds', opts.agentIds.join(','));
    const qs = params.toString();
    const response = await fetchWithAuth(
      `${API_BASE}/api/v1/identity/aws-role/trust-policy${qs ? `?${qs}` : ''}`,
    );
    const raw = await handleResponse<{
      success: true;
      data: import('../types/identity').AwsRoleTrustPolicy;
    }>(response);
    return raw.data;
  },

  async providers(): Promise<{ providers: import('../types/identity').ProviderCatalogEntry[] }> {
    const response = await fetchWithAuth(`${API_BASE}/api/v1/identity/providers`);
    const raw = await handleResponse<{
      success: true;
      data: { providers: import('../types/identity').ProviderCatalogEntry[] };
    }>(response);
    return raw.data;
  },

  /**
   * User-scope credentials — the caller's own creds, identified by the
   * `user` principal kind. Endpoints live under `/me/credentials/...`
   * and `/oauth/{provider}/start`. There's no path parameter because
   * the user is resolved from the JWT.
   *
   * All four endpoints share the same `{ success: true, data: ... }`
   * envelope as the rest of `identityApi`. If you change that on the
   * backend, fix it there — don't loosen the frontend.
   */
  me: {
    async credentials(): Promise<import('../types/identity').CredentialListResponse> {
      const response = await fetchWithAuth(`${API_BASE}/api/v1/me/credentials`);
      const raw = await handleResponse<{
        success: true;
        data: import('../types/identity').CredentialListResponse;
      }>(response);
      return raw.data;
    },

    async apiKey(
      body: import('../types/identity').ApiKeyStoreRequest,
    ): Promise<{ credential: import('../types/identity').Credential }> {
      const response = await fetchWithAuth(
        `${API_BASE}/api/v1/me/credentials/api-key`,
        { method: 'POST', body: JSON.stringify(body) },
      );
      const raw = await handleResponse<{
        success: true;
        data: { credential: import('../types/identity').Credential };
      }>(response);
      return raw.data;
    },

    async oauthManual(
      body: import('../types/identity').OAuthManualStoreRequest,
    ): Promise<{ credential: import('../types/identity').Credential }> {
      const response = await fetchWithAuth(
        `${API_BASE}/api/v1/me/credentials/oauth-manual`,
        { method: 'POST', body: JSON.stringify(body) },
      );
      const raw = await handleResponse<{
        success: true;
        data: { credential: import('../types/identity').Credential };
      }>(response);
      return raw.data;
    },

    /**
     * Start a user-scope OAuth flow. The provider sits in the URL
     * (`/oauth/{provider}/start`), not nested under `/me/credentials`,
     * because the new identity API treats provider-OAuth as a
     * top-level user-context flow.
     */
    async oauthStart(
      body: import('../types/identity').OAuthStartRequest,
    ): Promise<import('../types/identity').OAuthStartResponse> {
      const { provider, ...rest } = body;
      const response = await fetchWithAuth(
        `${API_BASE}/api/v1/oauth/${encodeURIComponent(provider)}/start`,
        { method: 'POST', body: JSON.stringify(rest) },
      );
      const raw = await handleResponse<{
        success: true;
        data: import('../types/identity').OAuthStartResponse;
      }>(response);
      return raw.data;
    },
  },

  space(spaceId: string) {
    return {
      async credentials(): Promise<import('../types/identity').CredentialListResponse> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/spaces/${spaceId}/credentials`,
        );
        const raw = await handleResponse<{
          success: true;
          data: import('../types/identity').CredentialListResponse;
        }>(response);
        return raw.data;
      },

      async oauthStart(
        body: import('../types/identity').OAuthStartRequest,
      ): Promise<import('../types/identity').OAuthStartResponse> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/spaces/${spaceId}/credentials/oauth-start`,
          { method: 'POST', body: JSON.stringify(body) },
        );
        const raw = await handleResponse<{
          success: true;
          data: import('../types/identity').OAuthStartResponse;
        }>(response);
        return raw.data;
      },

      async apiKey(
        body: import('../types/identity').ApiKeyStoreRequest,
      ): Promise<{ credential: import('../types/identity').Credential }> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/spaces/${spaceId}/credentials/api-key`,
          { method: 'POST', body: JSON.stringify(body) },
        );
        const raw = await handleResponse<{
          success: true;
          data: { credential: import('../types/identity').Credential };
        }>(response);
        return raw.data;
      },

      /**
       * Store a pre-obtained OAuth token bundle. Identity force-refreshes the
       * pasted refresh token on submit, taking sole ownership — the local copy
       * the user pasted from will stop working. Surfaces HTTP 422 when the
       * refresh fails (token already used or expired).
       */
      async oauthManual(
        body: import('../types/identity').OAuthManualStoreRequest,
      ): Promise<{ credential: import('../types/identity').Credential }> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/spaces/${spaceId}/credentials/oauth-manual`,
          { method: 'POST', body: JSON.stringify(body) },
        );
        const raw = await handleResponse<{
          success: true;
          data: { credential: import('../types/identity').Credential };
        }>(response);
        return raw.data;
      },

      /**
       * Attach an AWS IAM role to this (shared) space. Identity runs a
       * simulated AssumeRole before saving; a trust-policy rejection still
       * saves the role with `status: 'error'` and returns the AWS message.
       */
      async awsRole(
        body: import('../types/identity').AwsRoleStoreRequest,
      ): Promise<import('../types/identity').AwsRoleStoreResponse> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/spaces/${spaceId}/credentials/aws-role`,
          { method: 'POST', body: JSON.stringify(body) },
        );
        const raw = await handleResponse<{
          success: true;
          data: import('../types/identity').AwsRoleStoreResponse;
        }>(response);
        return raw.data;
      },
    };
  },

  org: {
    async credentials(): Promise<import('../types/identity').CredentialListResponse> {
      const response = await fetchWithAuth(`${API_BASE}/api/v1/org/credentials`);
      const raw = await handleResponse<{
        success: true;
        data: import('../types/identity').CredentialListResponse;
      }>(response);
      return raw.data;
    },

    async serviceAccount(
      body: import('../types/identity').OrgServiceAccountRequest,
    ): Promise<{ credential: import('../types/identity').Credential }> {
      const response = await fetchWithAuth(
        `${API_BASE}/api/v1/org/credentials/service-account`,
        { method: 'PUT', body: JSON.stringify(body) },
      );
      const raw = await handleResponse<{
        success: true;
        data: { credential: import('../types/identity').Credential };
      }>(response);
      return raw.data;
    },

    /**
     * Register an app/bot service-account credential (e.g. the Operator Google
     * Chat app SA key) used for posting "as the bot". Admin-only.
     */
    async appCredential(
      body: import('../types/identity').OrgAppCredentialRequest,
    ): Promise<{ credential: import('../types/identity').Credential }> {
      const response = await fetchWithAuth(
        `${API_BASE}/api/v1/org/credentials/app`,
        { method: 'PUT', body: JSON.stringify(body) },
      );
      const raw = await handleResponse<{
        success: true;
        data: { credential: import('../types/identity').Credential };
      }>(response);
      return raw.data;
    },

    /** Org tier of the manual-OAuth (paste-token) flow. See space.oauthManual. */
    async oauthManual(
      body: import('../types/identity').OAuthManualStoreRequest,
    ): Promise<{ credential: import('../types/identity').Credential }> {
      const response = await fetchWithAuth(
        `${API_BASE}/api/v1/org/credentials/oauth-manual`,
        { method: 'POST', body: JSON.stringify(body) },
      );
      const raw = await handleResponse<{
        success: true;
        data: { credential: import('../types/identity').Credential };
      }>(response);
      return raw.data;
    },

    /** Org-wide AWS IAM role (last tier of AWS resolution). Admin-only. */
    async awsRole(
      body: import('../types/identity').AwsRoleStoreRequest,
    ): Promise<import('../types/identity').AwsRoleStoreResponse> {
      const response = await fetchWithAuth(`${API_BASE}/api/v1/org/credentials/aws-role`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      const raw = await handleResponse<{
        success: true;
        data: import('../types/identity').AwsRoleStoreResponse;
      }>(response);
      return raw.data;
    },

    /**
     * Schema-driven org-level provider configuration. The index lists every
     * configurable provider with a per-org `configured` badge; per-provider
     * GET/PUT/DELETE load, save and clear the values. All org-admin only;
     * non-admins get 403. See optr-identity/.../provider-config-ui.md.
     */
    async providerConfigs(): Promise<{
      providers: import('../types/identity').ProviderConfigIndexEntry[];
    }> {
      const response = await fetchWithAuth(
        `${API_BASE}/api/v1/org/providers/config`,
      );
      const raw = await handleResponse<{
        success: true;
        data: { providers: import('../types/identity').ProviderConfigIndexEntry[] };
      }>(response);
      return raw.data;
    },

    providerConfig(providerId: string) {
      const base = `${API_BASE}/api/v1/org/providers/${encodeURIComponent(
        providerId,
      )}/config`;
      return {
        async get(): Promise<import('../types/identity').ProviderConfigDetail> {
          const response = await fetchWithAuth(base);
          const raw = await handleResponse<{
            success: true;
            data: import('../types/identity').ProviderConfigDetail;
          }>(response);
          return raw.data;
        },

        /**
         * Save config. Non-secret fields replace wholesale; secret keys merge
         * (omitting a secret key preserves the stored value). Returns the same
         * masked shape as GET, so no re-fetch is needed. 400 on validation.
         */
        async save(body: {
          values: Record<string, string>;
        }): Promise<import('../types/identity').ProviderConfigDetail> {
          const response = await fetchWithAuth(base, {
            method: 'PUT',
            body: JSON.stringify(body),
          });
          const raw = await handleResponse<{
            success: true;
            data: import('../types/identity').ProviderConfigDetail;
          }>(response);
          return raw.data;
        },

        async remove(): Promise<{ providerId: string; deleted: boolean }> {
          const response = await fetchWithAuth(base, { method: 'DELETE' });
          const raw = await handleResponse<{
            success: true;
            data: { providerId: string; deleted: boolean };
          }>(response);
          return raw.data;
        },
      };
    },
  },

  /**
   * Flat per-credential operations. Authz branches on the credential's
   * principal kind, enforced server-side.
   */
  credential(credId: string) {
    return {
      /**
       * Test a stored credential. For `aws-role` credentials `simulation`
       * picks the agent (and, for org roles, the space) to simulate the
       * AssumeRole as; other providers ignore it.
       */
      async verify(
        simulation?: import('../types/identity').AwsRoleSimulation,
      ): Promise<import('../types/identity').VerifyResult> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/credentials/${credId}/verify`,
          {
            method: 'POST',
            ...(simulation && { body: JSON.stringify(simulation) }),
          },
        );
        const raw = await handleResponse<{
          success: true;
          data: import('../types/identity').VerifyResult;
        }>(response);
        return raw.data;
      },

      async revoke(): Promise<{ revoked: true }> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/credentials/${credId}/revoke`,
          { method: 'POST' },
        );
        const raw = await handleResponse<{
          success: true;
          data: { revoked: true };
        }>(response);
        return raw.data;
      },

      async setDefault(): Promise<{ credential: import('../types/identity').Credential }> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/credentials/${credId}/set-default`,
          { method: 'POST' },
        );
        const raw = await handleResponse<{
          success: true;
          data: { credential: import('../types/identity').Credential };
        }>(response);
        return raw.data;
      },

      async rename(
        accountName: string,
      ): Promise<{ credential: import('../types/identity').Credential }> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/credentials/${credId}`,
          { method: 'PATCH', body: JSON.stringify({ accountName }) },
        );
        const raw = await handleResponse<{
          success: true;
          data: { credential: import('../types/identity').Credential };
        }>(response);
        return raw.data;
      },
    };
  },
};

/**
 * Top-level conn-integrations API. The provider catalog is org-global; the
 * org integration list is at `/api/v1/conn/org/integrations`. Routing-rule
 * CRUD and event delivery views live under `/api/v1/conn/org/integrations/{id}/...`
 * (dedicated org routes, no space anchor). get/patch/delete still go through
 * the space-scoped CRUD routes with an anchor spaceId authz gate.
 */
export const connApi = {
  /** Provider catalog — org-global, no space anchor needed. */
  providers: {
    async list(): Promise<Provider[]> {
      const response = await fetchWithAuth(`${API_BASE}/api/v1/conn/org/providers`);
      const raw = await handleResponse<{ data: { providers: Provider[]; count: number } }>(response);
      return raw.data?.providers ?? [];
    },
    async getSetupGuide(name: string): Promise<string> {
      const response = await fetchWithAuth(
        `${API_BASE}/api/v1/conn/org/providers/${encodeURIComponent(name)}/setup-guide`,
      );
      // Setup-guide returns raw markdown, not the JSON envelope — don't
      // run it through `handleResponse`.
      if (!response.ok) {
        throw new Error(`Failed to load setup guide for ${name} (${response.status})`);
      }
      return response.text();
    },
    // 404 = provider has no quick guide; resolve to null so the panel can
    // silently skip rendering. Other errors still throw.
    async getQuickSetupGuide(name: string): Promise<string | null> {
      const response = await fetchWithAuth(
        `${API_BASE}/api/v1/conn/org/providers/${encodeURIComponent(name)}/quick-setup-guide`,
      );
      if (response.status === 404) return null;
      if (!response.ok) {
        throw new Error(`Failed to load quick setup guide for ${name} (${response.status})`);
      }
      return response.text();
    },
  },

  /** Google Workspace org connection management (admin). */
  google: {
    /** Org Google connections are org integrations with ingestionType google-push. */
    async listConnections(): Promise<Integration[]> {
      const all = await connApi.org.integrations.list();
      return all.filter((i) => i.ingestionType === 'google-push');
    },
    async createConnection(
      input: import('../types/google').CreateGoogleConnectionInput,
    ): Promise<import('../types/google').CreateGoogleConnectionResult> {
      const response = await fetchWithAuth(
        `${API_BASE}/api/v1/conn/org/google/connections`,
        { method: 'POST', body: JSON.stringify(input) },
      );
      const raw = await handleResponse<
        ApiResponse<import('../types/google').CreateGoogleConnectionResult>
      >(response);
      return raw.data!;
    },
    async listOrgWatches(
      connectionId: string,
    ): Promise<import('../types/google').GoogleWatch[]> {
      const response = await fetchWithAuth(
        `${API_BASE}/api/v1/conn/org/google/connections/${connectionId}/watches`,
      );
      const raw = await handleResponse<
        ApiResponse<import('../types/google').GoogleWatch[]>
      >(response);
      return raw.data ?? [];
    },
    /** Detect the admin's Chat bot DMs as `chatAppId` candidates (org-admin). */
    async detectChatApp(
      accountId?: string,
    ): Promise<import('../types/google').ChatAppCandidate[]> {
      const qs = accountId ? `?accountId=${encodeURIComponent(accountId)}` : '';
      const response = await fetchWithAuth(
        `${API_BASE}/api/v1/conn/org/google/chat/app-candidates${qs}`,
      );
      const raw = await handleResponse<
        ApiResponse<import('../types/google').ChatAppCandidate[]>
      >(response);
      return raw.data ?? [];
    },
  },

  org: {
    integrations: {
      /** Org-wide integration list. Backend optionally filters by ?provider. */
      async list(opts?: { provider?: string }): Promise<Integration[]> {
        const qs = opts?.provider ? `?provider=${encodeURIComponent(opts.provider)}` : '';
        const response = await fetchWithAuth(`${API_BASE}/api/v1/conn/org/integrations${qs}`);
        const raw = await handleResponse<{ data: { integrations: Integration[]; count: number } }>(response);
        return raw.data?.integrations ?? [];
      },

      /**
       * Org-scoped create. The deployed API has no `POST /conn/org/integrations`
       * — creation always goes through the space-scoped route, with `scope:
       * 'org'` in the body. The anchor space the admin posts through doesn't
       * own the resulting integration; it's just an authz gate.
       */
      async create(
        anchorSpaceId: string,
        input: Omit<CreateIntegrationInput, 'scope'>,
      ): Promise<Integration> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/spaces/${anchorSpaceId}/conn/integrations`,
          {
            method: 'POST',
            body: JSON.stringify({ ...input, scope: 'org' }),
          },
        );
        const raw = await handleResponse<ApiResponse<Integration>>(response);
        return raw.data!;
      },

      /**
       * `get` / `update` still take an anchor spaceId because there are no
       * dedicated org routes for them yet — the URL spaceId is an authz gate,
       * the lookup happens by integrationId. `delete` and events have moved
       * to the dedicated org routes.
       */
      async get(anchorSpaceId: string, integrationId: string): Promise<Integration> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/spaces/${anchorSpaceId}/conn/integrations/${integrationId}`,
        );
        const raw = await handleResponse<ApiResponse<Integration>>(response);
        return raw.data!;
      },
      async update(
        integrationId: string,
        input: UpdateIntegrationInput,
      ): Promise<Integration> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/conn/org/integrations/${integrationId}`,
          { method: 'PATCH', body: JSON.stringify(input) },
        );
        const raw = await handleResponse<ApiResponse<Integration>>(response);
        return raw.data!;
      },
      async delete(integrationId: string): Promise<void> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/conn/org/integrations/${integrationId}`,
          { method: 'DELETE' },
        );
        await handleResponse(response);
      },
      // Events live on the dedicated org route (like routing rules), NOT the
      // space-anchored path. The space-anchored `/events` sub-resource 404s
      // for org-scoped integrations — the anchor space doesn't own them.
      async listEvents(integrationId: string): Promise<IntegrationEvent[]> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/conn/org/integrations/${integrationId}/events`,
        );
        const raw = await handleResponse<{ data: { events: IntegrationEvent[]; count: number } }>(response);
        return raw.data?.events ?? [];
      },
      async getEvent(
        integrationId: string,
        eventId: string,
      ): Promise<IntegrationEventDetail> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/conn/org/integrations/${integrationId}/events/${eventId}`,
        );
        const raw = await handleResponse<ApiResponse<IntegrationEventDetail>>(response);
        return raw.data!;
      },
      async redeliverEvent(
        integrationId: string,
        eventId: string,
      ): Promise<IntegrationRedeliverResult> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/conn/org/integrations/${integrationId}/events/${eventId}/redeliver`,
          { method: 'POST' },
        );
        const raw = await handleResponse<ApiResponse<IntegrationRedeliverResult>>(response);
        return raw.data!;
      },

      // ----- Routing rules — dedicated org routes (no anchor spaceId). -----

      async listRules(integrationId: string): Promise<RoutingRule[]> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/conn/org/integrations/${integrationId}/rules`,
        );
        // Server returns `{ success: true, data: [...] }` — bare array under `data`.
        const raw = await handleResponse<ApiResponse<RoutingRule[]>>(response);
        return raw.data ?? [];
      },
      async createRule(
        integrationId: string,
        input: CreateRoutingRuleInput,
      ): Promise<RoutingRule> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/conn/org/integrations/${integrationId}/rules`,
          { method: 'POST', body: JSON.stringify(input) },
        );
        const raw = await handleResponse<ApiResponse<RoutingRule>>(response);
        return raw.data!;
      },
      async deleteRule(
        integrationId: string,
        ruleId: string,
      ): Promise<{ deleted: true; ruleId: string }> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/conn/org/integrations/${integrationId}/rules/${ruleId}`,
          { method: 'DELETE' },
        );
        const raw = await handleResponse<ApiResponse<{ deleted: true; ruleId: string }>>(response);
        return raw.data!;
      },
    },
  },
};

/**
 * MCP provider catalog — org-global, no space anchor. Drives the curated
 * picker on the "Add MCP server" flow. The catalog is content-only; install
 * persistence still goes through `spaceApi(...).mcpServers.create` (and the
 * org equivalent).
 */
export const mcpCatalogApi = {
  async list(): Promise<McpProvidersResponse> {
    const response = await fetchWithAuth(`${API_BASE}/api/v1/mcp-providers`);
    const raw = await handleResponse<ApiResponse<McpProvidersResponse>>(response);
    return raw.data!;
  },
};

/**
 * Space-scoped API factory. Returns an object whose methods prepend
 * `/api/v1/spaces/{spaceId}` to the path. Shape mirrors the legacy flat
 * exports so the migration is a call-site swap.
 *
 * Hooks typically wrap this in `useMemo(() => spaceApi(spaceId), [spaceId])`
 * so the returned object identity is stable per space.
 */
/**
 * Presign a direct, per-run WebSocket ticket for a conversation. NOT
 * space-scoped — the backend derives space/agent/tenancy from the conversation
 * + auth — so this needs only the conversation id and can run before any
 * space/list data has loaded. Pass `{ agentId }` when minting a brand-new
 * conversation; omit for resume. See `src/utils/direct-run-connection.ts`.
 */
export async function wsPresignConversation(
  conversationId: string,
  body: { agentId?: string } = {},
): Promise<WsPresignTicket> {
  const response = await fetchWithAuth(
    `${API_BASE}/api/v1/conversations/${conversationId}/ws-presign`,
    { method: 'POST', body: JSON.stringify(body) },
  );
  // Response is flat (no `{ data }` wrapper), but tolerate both.
  const raw = await handleResponse<WsPresignTicket | ApiResponse<WsPresignTicket>>(response);
  return 'wsPath' in raw ? raw : raw.data!;
}

export function spaceApi(spaceId: string) {
  const base = `${API_BASE}/api/v1/spaces/${spaceId}`;

  return {
    spaceId,

    space: {
      async get(): Promise<{ space: Space; role: import('../types/spaces').SpaceRole }> {
        const response = await fetchWithAuth(`${API_BASE}/api/v1/spaces/${spaceId}`);
        const data = await handleResponse<
          ApiResponse<{ space: Space; role: import('../types/spaces').SpaceRole }>
        >(response);
        return data.data!;
      },

      /** Rename, edit description, or archive (`{ archived: true }`).
       *  Admin+ for name/description; Owner-only for archived: true.
       *  Personal Spaces can't be archived. */
      async update(patch: {
        name?: string;
        description?: string;
        archived?: boolean;
      }): Promise<Space> {
        const response = await fetchWithAuth(`${API_BASE}/api/v1/spaces/${spaceId}`, {
          method: 'PATCH',
          body: JSON.stringify(patch),
        });
        const data = await handleResponse<ApiResponse<{ space: Space }>>(response);
        return data.data!.space;
      },

      /** Restore an archived space. Admin or Owner. */
      async unarchive(): Promise<Space> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/spaces/${spaceId}/unarchive`,
          { method: 'POST' },
        );
        const data = await handleResponse<ApiResponse<{ space: Space }>>(response);
        return data.data!.space;
      },

      /** Promote an existing member to owner; demote current owner to admin.
       *  Owner-only. May return 409 on transaction race — caller retries once. */
      async transferOwnership(userId: string): Promise<{
        space: Space;
        previousOwnerUserId: string;
        newOwnerUserId: string;
      }> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/spaces/${spaceId}/transfer-ownership`,
          { method: 'POST', body: JSON.stringify({ userId }) },
        );
        const data = await handleResponse<
          ApiResponse<{
            space: Space;
            previousOwnerUserId: string;
            newOwnerUserId: string;
          }>
        >(response);
        return data.data!;
      },
    },

    members: {
      /** Admin+ only. Returns flat array (note: not paginated). */
      async list(): Promise<import('../types/spaces').SpaceMembership[]> {
        const response = await fetchWithAuth(`${API_BASE}/api/v1/spaces/${spaceId}/members`);
        const data = await handleResponse<
          ApiResponse<{ members: import('../types/spaces').SpaceMembership[] }>
        >(response);
        return data.data?.members ?? [];
      },

      /** Add an existing org user. Admin+ only. Server rejects role: 'owner'
       *  — use `space.transferOwnership` instead. POST returns raw
       *  `{ membership }` (no `success` wrapper). */
      async add(input: {
        userId: string;
        role: 'admin' | 'member' | 'viewer';
      }): Promise<import('../types/spaces').SpaceMembership> {
        const response = await fetchWithAuth(`${API_BASE}/api/v1/spaces/${spaceId}/members`, {
          method: 'POST',
          body: JSON.stringify(input),
        });
        const raw = await handleResponse<{
          membership: import('../types/spaces').SpaceMembership;
        }>(response);
        return raw.membership;
      },

      /** Change a member's role. Admin+ only. Server rejects role: 'owner'. */
      async updateRole(
        userId: string,
        role: 'admin' | 'member' | 'viewer',
      ): Promise<import('../types/spaces').SpaceMembership> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/spaces/${spaceId}/members/${userId}`,
          { method: 'PATCH', body: JSON.stringify({ role }) },
        );
        const data = await handleResponse<
          ApiResponse<{ membership: import('../types/spaces').SpaceMembership }>
        >(response);
        return data.data!.membership;
      },

      /** Remove a member. Admin+ for others; non-admin can only self-remove.
       *  Returns 204 with empty body. */
      async remove(userId: string): Promise<void> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/spaces/${spaceId}/members/${userId}`,
          { method: 'DELETE' },
        );
        if (!response.ok) {
          // 204 has no body; only failure paths reach here.
          await handleResponse(response);
        }
      },

      /** Pin an auto-joined member (source 'auto' → 'manual') so auto-join
       *  rules no longer remove or re-role them. Admin+. Idempotent. */
      async pin(
        userId: string,
      ): Promise<import('../types/spaces').SpaceMembership> {
        const response = await fetchWithAuth(
          `${API_BASE}/api/v1/spaces/${spaceId}/members/${userId}/pin`,
          { method: 'POST' },
        );
        const data = await handleResponse<
          ApiResponse<{ membership: import('../types/spaces').SpaceMembership }>
        >(response);
        return data.data!.membership;
      },
    },

    /** Auto-join rules — attribute-driven membership. All Admin+. */
    autoJoinRules: {
      async list(): Promise<import('../types/spaces').SpaceAutoJoinRule[]> {
        const response = await fetchWithAuth(`${base}/auto-join-rules`);
        const data = await handleResponse<
          ApiResponse<{ rules: import('../types/spaces').SpaceAutoJoinRule[] }>
        >(response);
        return data.data?.rules ?? [];
      },

      async create(
        input: import('../types/spaces').CreateAutoJoinRuleInput,
      ): Promise<import('../types/spaces').SpaceAutoJoinRule> {
        const response = await fetchWithAuth(`${base}/auto-join-rules`, {
          method: 'POST',
          body: JSON.stringify(input),
        });
        const data = await handleResponse<
          ApiResponse<{ rule: import('../types/spaces').SpaceAutoJoinRule }>
        >(response);
        return data.data!.rule;
      },

      async update(
        ruleId: string,
        input: import('../types/spaces').UpdateAutoJoinRuleInput,
      ): Promise<import('../types/spaces').SpaceAutoJoinRule> {
        const response = await fetchWithAuth(
          `${base}/auto-join-rules/${ruleId}`,
          { method: 'PATCH', body: JSON.stringify(input) },
        );
        const data = await handleResponse<
          ApiResponse<{ rule: import('../types/spaces').SpaceAutoJoinRule }>
        >(response);
        return data.data!.rule;
      },

      async remove(ruleId: string): Promise<void> {
        const response = await fetchWithAuth(
          `${base}/auto-join-rules/${ruleId}`,
          { method: 'DELETE' },
        );
        if (!response.ok) await handleResponse(response);
      },

      /** Apply rules across the org now (async; returns 202). */
      async apply(): Promise<void> {
        const response = await fetchWithAuth(`${base}/auto-join-rules/apply`, {
          method: 'POST',
        });
        if (!response.ok) await handleResponse(response);
      },

      /** Re-run rules for a single user now (sync). Remediates a missed
       *  reconcile event without resorting to a sticky manual add. */
      async reconcileUser(
        userId: string,
      ): Promise<import('../types/spaces').ReconcileUserResult> {
        const response = await fetchWithAuth(
          `${base}/auto-join-rules/reconcile-user`,
          { method: 'POST', body: JSON.stringify({ userId }) },
        );
        const data = await handleResponse<
          ApiResponse<import('../types/spaces').ReconcileUserResult>
        >(response);
        return data.data!;
      },
    },

    agents: {
      async list(): Promise<PaginatedResponse<Agent>> {
        const response = await fetchWithAuth(`${base}/agents?limit=500`);
        const raw = await handleResponse<{ data: Agent[]; pagination: unknown }>(response);
        return { items: raw.data };
      },
      async get(agentId: string): Promise<Agent> {
        const response = await fetchWithAuth(`${base}/agents/${agentId}`);
        const data = await handleResponse<ApiResponse<Agent>>(response);
        return data.data!;
      },
      async create(input: CreateAgentInput): Promise<Agent> {
        const response = await fetchWithAuth(`${base}/agents`, {
          method: 'POST',
          body: JSON.stringify(input),
        });
        const data = await handleResponse<ApiResponse<Agent>>(response);
        return data.data!;
      },
      async update(agentId: string, input: UpdateAgentInput): Promise<Agent> {
        const response = await fetchWithAuth(`${base}/agents/${agentId}`, {
          method: 'PUT',
          body: JSON.stringify(input),
        });
        const data = await handleResponse<ApiResponse<Agent>>(response);
        return data.data!;
      },
      async delete(agentId: string): Promise<void> {
        const response = await fetchWithAuth(`${base}/agents/${agentId}`, { method: 'DELETE' });
        await handleResponse(response);
      },
      async publish(agentId: string, description?: string): Promise<AgentVersion> {
        const response = await fetchWithAuth(`${base}/agents/${agentId}/publish`, {
          method: 'POST',
          body: JSON.stringify({ description }),
        });
        const data = await handleResponse<ApiResponse<AgentVersion>>(response);
        return data.data!;
      },
      async listVersions(agentId: string): Promise<PaginatedResponse<AgentVersion>> {
        const response = await fetchWithAuth(`${base}/agents/${agentId}/versions`);
        const raw = await handleResponse<{
          data: AgentVersion[];
          pagination?: { nextCursor: string | null };
        }>(response);
        return {
          items: raw.data ?? [],
          nextToken: raw.pagination?.nextCursor ?? undefined,
        };
      },
      async getVersion(agentId: string, version: number): Promise<AgentVersion> {
        const response = await fetchWithAuth(`${base}/agents/${agentId}/versions/${version}`);
        const data = await handleResponse<ApiResponse<AgentVersion>>(response);
        return data.data!;
      },
      async deleteVersion(agentId: string, version: number): Promise<void> {
        const response = await fetchWithAuth(`${base}/agents/${agentId}/versions/${version}`, {
          method: 'DELETE',
        });
        await handleResponse(response);
      },
      async listCredentialGrants(
        agentId: string,
      ): Promise<import('../types/agent-credentials').AgentCredentialGrant[]> {
        const response = await fetchWithAuth(`${base}/agents/${agentId}/credentials`);
        const data = await handleResponse<
          ApiResponse<{ grants: import('../types/agent-credentials').AgentCredentialGrant[] }>
        >(response);
        return data.data?.grants ?? [];
      },
      async listGrantableCredentials(
        agentId: string,
      ): Promise<import('../types/agent-credentials').GrantableCredential[]> {
        const response = await fetchWithAuth(`${base}/agents/${agentId}/credentials/grantable`);
        const data = await handleResponse<
          ApiResponse<{ credentials: import('../types/agent-credentials').GrantableCredential[] }>
        >(response);
        return data.data?.credentials ?? [];
      },
      /** Replaces the agent's whole grant set (`[]` removes all). */
      async setCredentialGrants(
        agentId: string,
        grants: import('../types/agent-credentials').AgentCredentialGrantInput[],
      ): Promise<import('../types/agent-credentials').AgentCredentialGrant[]> {
        const response = await fetchWithAuth(`${base}/agents/${agentId}/credentials`, {
          method: 'PUT',
          body: JSON.stringify({ grants }),
        });
        const data = await handleResponse<
          ApiResponse<{ grants: import('../types/agent-credentials').AgentCredentialGrant[] }>
        >(response);
        return data.data?.grants ?? [];
      },
      async listAliases(agentId: string): Promise<PaginatedResponse<AgentAlias>> {
        const response = await fetchWithAuth(`${base}/agents/${agentId}/aliases`);
        const raw = await handleResponse<{
          data: AgentAlias[];
          pagination?: { nextCursor: string | null };
        }>(response);
        return {
          items: raw.data ?? [],
          nextToken: raw.pagination?.nextCursor ?? undefined,
        };
      },
      async createAlias(
        agentId: string,
        aliasName: string,
        targetVersion: number,
        description?: string,
      ): Promise<AgentAlias> {
        const response = await fetchWithAuth(`${base}/agents/${agentId}/aliases`, {
          method: 'POST',
          body: JSON.stringify({ aliasName, targetVersion, description }),
        });
        const data = await handleResponse<ApiResponse<AgentAlias>>(response);
        return data.data!;
      },
      async getAlias(agentId: string, aliasName: string): Promise<AgentAlias> {
        const response = await fetchWithAuth(`${base}/agents/${agentId}/aliases/${aliasName}`);
        const data = await handleResponse<ApiResponse<AgentAlias>>(response);
        return data.data!;
      },
      async deleteAlias(agentId: string, aliasName: string): Promise<void> {
        const response = await fetchWithAuth(`${base}/agents/${agentId}/aliases/${aliasName}`, {
          method: 'DELETE',
        });
        await handleResponse(response);
      },
      async invoke(agentId: string, input: InvokeInput): Promise<InvokeResponse> {
        const response = await fetchWithAuth(`${base}/agents/${agentId}/invoke`, {
          method: 'POST',
          body: JSON.stringify(input),
        });
        const data = await handleResponse<ApiResponse<InvokeResponse>>(response);
        return data.data!;
      },
      async invokeAsync(agentId: string, input: InvokeInput): Promise<InvokeAsyncResponse> {
        const response = await fetchWithAuth(`${base}/agents/${agentId}/invoke-async`, {
          method: 'POST',
          body: JSON.stringify(input),
        });
        return handleResponse<InvokeAsyncResponse>(response);
      },
      async dispatchAction(
        agentId: string,
        input: DispatchActionInput,
      ): Promise<DispatchActionResponse> {
        const response = await fetchWithAuth(`${base}/agents/${agentId}/dispatch-action`, {
          method: 'POST',
          body: JSON.stringify(input),
        });
        return handleResponse<DispatchActionResponse>(response);
      },
    },

    skills: {
      async list(): Promise<PaginatedResponse<Skill>> {
        const response = await fetchWithAuth(`${base}/skills?limit=500`);
        const raw = await handleResponse<{ data: Skill[]; pagination: unknown }>(response);
        return { items: raw.data };
      },
      async get(skillId: string): Promise<Skill> {
        const response = await fetchWithAuth(`${base}/skills/${skillId}`);
        const data = await handleResponse<ApiResponse<Skill>>(response);
        return data.data!;
      },
      async create(input: CreateSkillInput): Promise<Skill> {
        const response = await fetchWithAuth(`${base}/skills`, {
          method: 'POST',
          body: JSON.stringify(input),
        });
        const data = await handleResponse<ApiResponse<Skill>>(response);
        return data.data!;
      },
      async update(skillId: string, input: UpdateSkillInput): Promise<Skill> {
        const response = await fetchWithAuth(`${base}/skills/${skillId}`, {
          method: 'PUT',
          body: JSON.stringify(input),
        });
        const data = await handleResponse<ApiResponse<Skill>>(response);
        return data.data!;
      },
      async delete(skillId: string): Promise<void> {
        const response = await fetchWithAuth(`${base}/skills/${skillId}`, { method: 'DELETE' });
        await handleResponse(response);
      },
    },

    plugins: {
      async list(): Promise<PaginatedResponse<Plugin>> {
        const response = await fetchWithAuth(`${base}/plugins?limit=100`);
        const raw = await handleResponse<{ data: Plugin[]; pagination: unknown }>(response);
        return { items: raw.data };
      },
      async get(
        pluginId: string,
        opts: { expand?: ('skills' | 'agents')[] } = {},
      ): Promise<Plugin> {
        const q = opts.expand?.length ? `?expand=${opts.expand.join(',')}` : '';
        const response = await fetchWithAuth(`${base}/plugins/${pluginId}${q}`);
        const data = await handleResponse<ApiResponse<Plugin>>(response);
        return data.data!;
      },
      /**
       * Read-only repo inspection. No Plugin row is created — call freely
       * as the user types/pastes a URL or changes the ref dropdown.
       */
      async explore(input: ExploreRequest): Promise<ExploreResponse> {
        const response = await fetchWithAuth(`${base}/plugins/explore`, {
          method: 'POST',
          body: JSON.stringify(input),
        });
        const data = await handleResponse<ApiResponse<ExploreResponse>>(response);
        return data.data!;
      },
      /** 202 → PluginInstallAck (plugin mode); 200 → PluginInstallReport (import mode). */
      async install(input: InstallPluginInput): Promise<PluginInstallResult> {
        const response = await fetchWithAuth(`${base}/plugins`, {
          method: 'POST',
          body: JSON.stringify(input),
        });
        const data = await handleResponse<ApiResponse<PluginInstallResult>>(response);
        return data.data!;
      },
      async switchRef(pluginId: string, ref: string): Promise<Plugin> {
        const response = await fetchWithAuth(`${base}/plugins/${pluginId}/ref`, {
          method: 'POST',
          body: JSON.stringify({ ref }),
        });
        const data = await handleResponse<ApiResponse<Plugin>>(response);
        return data.data!;
      },
      /**
       * Returns just the refreshed `upstream` block (not a full Plugin).
       * The hook invalidates the plugin cache after success, so a follow-up
       * GET picks up the new upstream values.
       */
      async checkUpdates(pluginId: string): Promise<PluginUpstream> {
        const response = await fetchWithAuth(`${base}/plugins/${pluginId}/check-updates`, {
          method: 'POST',
        });
        const data = await handleResponse<ApiResponse<PluginUpstream>>(response);
        return data.data!;
      },
      async sync(pluginId: string): Promise<SyncAckResponse> {
        const response = await fetchWithAuth(`${base}/plugins/${pluginId}/sync`, {
          method: 'POST',
        });
        const data = await handleResponse<ApiResponse<SyncAckResponse>>(response);
        return data.data!;
      },
      /**
       * Flip the auto-update flag. Passive: the next ~6-hourly scan acts on
       * the new value; this call does not enqueue a sync. Returns the updated
       * Plugin so the hook can write it straight into cache.
       */
      async setAutoUpdate(
        pluginId: string,
        autoUpdate: boolean,
      ): Promise<Plugin> {
        const response = await fetchWithAuth(
          `${base}/plugins/${pluginId}/auto-update`,
          { method: 'POST', body: JSON.stringify({ autoUpdate }) },
        );
        const data = await handleResponse<ApiResponse<Plugin>>(response);
        return data.data!;
      },
      async uninstall(pluginId: string): Promise<void> {
        const response = await fetchWithAuth(`${base}/plugins/${pluginId}/uninstall`, {
          method: 'POST',
        });
        await handleResponse(response);
      },
    },

    tools: {
      async list(): Promise<PaginatedResponse<Tool>> {
        const response = await fetchWithAuth(`${base}/tools?limit=500`);
        const raw = await handleResponse<{ data: Tool[]; pagination: unknown }>(response);
        return { items: raw.data };
      },
      async get(toolId: string): Promise<Tool> {
        const response = await fetchWithAuth(`${base}/tools/${toolId}`);
        const data = await handleResponse<ApiResponse<Tool>>(response);
        return data.data!;
      },
      async create(input: CreateToolInput): Promise<Tool> {
        const response = await fetchWithAuth(`${base}/tools`, {
          method: 'POST',
          body: JSON.stringify(input),
        });
        const data = await handleResponse<ApiResponse<Tool>>(response);
        return data.data!;
      },
      async update(toolId: string, input: UpdateToolInput): Promise<Tool> {
        const response = await fetchWithAuth(`${base}/tools/${toolId}`, {
          method: 'PUT',
          body: JSON.stringify(input),
        });
        const data = await handleResponse<ApiResponse<Tool>>(response);
        return data.data!;
      },
      async delete(toolId: string): Promise<void> {
        const response = await fetchWithAuth(`${base}/tools/${toolId}`, { method: 'DELETE' });
        await handleResponse(response);
      },
      async grouped(): Promise<GroupedToolsResponse> {
        const response = await fetchWithAuth(`${base}/tools/grouped`);
        const raw = await handleResponse<GroupedToolsResponse & { data?: GroupedToolsResponse }>(response);
        return raw.groups ? raw : raw.data ?? raw;
      },
    },

    emails: {
      async list(): Promise<PaginatedResponse<Email>> {
        const response = await fetchWithAuth(`${base}/conn/emails`);
        const raw = await handleResponse<{ data: Email[]; pagination: unknown }>(response);
        return { items: raw.data };
      },
      async get(emailId: string): Promise<Email> {
        const response = await fetchWithAuth(`${base}/conn/emails/${emailId}`);
        const data = await handleResponse<ApiResponse<Email>>(response);
        return data.data!;
      },
      async create(input: CreateEmailInput): Promise<Email> {
        const response = await fetchWithAuth(`${base}/conn/emails`, {
          method: 'POST',
          body: JSON.stringify(input),
        });
        const data = await handleResponse<ApiResponse<Email>>(response);
        return data.data!;
      },
      async update(emailId: string, input: UpdateEmailInput): Promise<Email> {
        const response = await fetchWithAuth(`${base}/conn/emails/${emailId}`, {
          method: 'PUT',
          body: JSON.stringify(input),
        });
        const data = await handleResponse<ApiResponse<Email>>(response);
        return data.data!;
      },
      async delete(emailId: string): Promise<void> {
        const response = await fetchWithAuth(`${base}/conn/emails/${emailId}`, { method: 'DELETE' });
        await handleResponse(response);
      },
    },

    conversations: {
      async list(opts?: {
        limit?: number;
        cursor?: string;
        persistent?: boolean | 'all';
        origin?: 'user' | 'background' | 'all';
      }): Promise<PaginatedResponse<Conversation> & { pinned: Conversation[] }> {
        const search = new URLSearchParams();
        search.set('limit', String(opts?.limit ?? 30));
        if (opts?.cursor) search.set('cursor', opts.cursor);
        if (opts?.persistent !== undefined) search.set('persistent', String(opts.persistent));
        if (opts?.origin) search.set('origin', opts.origin);
        const response = await fetchWithAuth(`${base}/conversations?${search.toString()}`);
        const raw = await handleResponse<{
          data: Conversation[];
          pinned?: Conversation[];
          pagination: { count: number; nextCursor: string | null; hasMore: boolean };
        }>(response);
        return {
          items: raw.data ?? [],
          pinned: raw.pinned ?? [],
          nextToken: raw.pagination?.nextCursor ?? undefined,
        };
      },
      async getConversation(conversationId: string): Promise<ConversationWithMessages> {
        const response = await fetchWithAuth(`${base}/conversations/${conversationId}`);
        const data = await handleResponse<ApiResponse<ConversationWithMessages>>(response);
        return data.data!;
      },
      async create(input: CreateConversationInput): Promise<Conversation> {
        const response = await fetchWithAuth(`${base}/conversations`, {
          method: 'POST',
          body: JSON.stringify(input),
        });
        const data = await handleResponse<ApiResponse<Conversation>>(response);
        return data.data!;
      },
      async update(conversationId: string, input: UpdateConversationInput): Promise<Conversation> {
        const response = await fetchWithAuth(`${base}/conversations/${conversationId}`, {
          method: 'PATCH',
          body: JSON.stringify(input),
        });
        const data = await handleResponse<ApiResponse<Conversation>>(response);
        return data.data!;
      },
      async delete(conversationId: string): Promise<void> {
        const response = await fetchWithAuth(`${base}/conversations/${conversationId}`, {
          method: 'DELETE',
        });
        await handleResponse(response);
      },
    },

    integrations: {
      /**
       * Space discovery — which event types arrive in this space from org
       * routing rules. Renders the "Routed from org integrations" card on
       * the space integrations list.
       */
      async listRoutedEventTypes(): Promise<RoutedEventType[]> {
        const response = await fetchWithAuth(`${base}/conn/routed-event-types`);
        // Server envelope: `{ success: true, data: [...] }` — bare array under `data`.
        const raw = await handleResponse<ApiResponse<RoutedEventType[]>>(response);
        return raw.data ?? [];
      },
      /**
       * One row per org integration whose rules forward events into this
       * space. Backend filters by the caller's spaceId; rows include
       * lastEventAt + eventCount slices scoped to this space.
       *
       * Events for these integrations come from the same `listEvents` route
       * below — the backend filters by `matchedTargetSpaceIds` when the
       * caller doesn't own the integration.
       */
      async listSubscriptions(): Promise<Subscription[]> {
        const response = await fetchWithAuth(`${base}/conn/subscriptions`);
        const raw = await handleResponse<ApiResponse<Subscription[]>>(response);
        return raw.data ?? [];
      },
      async getSubscription(integrationId: string): Promise<Subscription> {
        const response = await fetchWithAuth(`${base}/conn/subscriptions/${integrationId}`);
        const raw = await handleResponse<ApiResponse<Subscription>>(response);
        return raw.data!;
      },
      async list(): Promise<Integration[]> {
        const response = await fetchWithAuth(`${base}/conn/integrations`);
        const raw = await handleResponse<{ data: { integrations: Integration[]; count: number } }>(response);
        return raw.data?.integrations ?? [];
      },
      async get(integrationId: string): Promise<Integration> {
        const response = await fetchWithAuth(`${base}/conn/integrations/${integrationId}`);
        const raw = await handleResponse<ApiResponse<Integration>>(response);
        return raw.data!;
      },
      async create(input: CreateIntegrationInput): Promise<Integration> {
        const response = await fetchWithAuth(`${base}/conn/integrations`, {
          method: 'POST',
          body: JSON.stringify(input),
        });
        const raw = await handleResponse<ApiResponse<Integration>>(response);
        return raw.data!;
      },
      async update(integrationId: string, input: UpdateIntegrationInput): Promise<Integration> {
        const response = await fetchWithAuth(`${base}/conn/integrations/${integrationId}`, {
          method: 'PATCH',
          body: JSON.stringify(input),
        });
        const raw = await handleResponse<ApiResponse<Integration>>(response);
        return raw.data!;
      },
      async delete(integrationId: string): Promise<void> {
        const response = await fetchWithAuth(`${base}/conn/integrations/${integrationId}`, {
          method: 'DELETE',
        });
        await handleResponse(response);
      },
      async listEvents(integrationId: string): Promise<IntegrationEvent[]> {
        const response = await fetchWithAuth(`${base}/conn/integrations/${integrationId}/events`);
        const raw = await handleResponse<{ data: { events: IntegrationEvent[]; count: number } }>(response);
        return raw.data?.events ?? [];
      },
      async getEvent(integrationId: string, eventId: string): Promise<IntegrationEventDetail> {
        const response = await fetchWithAuth(
          `${base}/conn/integrations/${integrationId}/events/${eventId}`,
        );
        const raw = await handleResponse<ApiResponse<IntegrationEventDetail>>(response);
        return raw.data!;
      },
      async redeliverEvent(
        integrationId: string,
        eventId: string,
      ): Promise<IntegrationRedeliverResult> {
        const response = await fetchWithAuth(
          `${base}/conn/integrations/${integrationId}/events/${eventId}/redeliver`,
          { method: 'POST' },
        );
        const raw = await handleResponse<ApiResponse<IntegrationRedeliverResult>>(response);
        return raw.data!;
      },
    },

    /** Google Workspace (Gmail + Chat + Calendar) per-space watch management. */
    google: {
      async listWatches(
        connectionId: string,
      ): Promise<import('../types/google').GoogleWatch[]> {
        const response = await fetchWithAuth(
          `${base}/conn/google/connections/${connectionId}/watches`,
        );
        const raw = await handleResponse<
          ApiResponse<import('../types/google').GoogleWatch[]>
        >(response);
        return raw.data ?? [];
      },
      /**
       * Calendar only: the calendars visible to the connecting Google account
       * (primary + secondary + shared), used to populate the watch picker.
       */
      async listCalendars(
        connectionId: string,
        opts?: { accountId?: string; principalKind?: 'user' | 'space-service' },
      ): Promise<import('../types/google').GoogleCalendar[]> {
        const qs = new URLSearchParams();
        if (opts?.accountId) qs.set('accountId', opts.accountId);
        if (opts?.principalKind) qs.set('principalKind', opts.principalKind);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        const response = await fetchWithAuth(
          `${base}/conn/google/connections/${connectionId}/calendars${suffix}`,
        );
        type CalList = { calendars?: import('../types/google').GoogleCalendar[] };
        const raw = await handleResponse<ApiResponse<CalList> & CalList>(response);
        // Tolerate either envelope: `{ data: { calendars } }` (matches the sibling
        // listWatches) or a flat `{ calendars }` body.
        return raw.data?.calendars ?? raw.calendars ?? [];
      },
      async connect(
        connectionId: string,
        input: import('../types/google').CreateGoogleWatchInput,
      ): Promise<import('../types/google').GoogleWatch> {
        const response = await fetchWithAuth(
          `${base}/conn/google/connections/${connectionId}/watches`,
          { method: 'POST', body: JSON.stringify(input) },
        );
        const raw = await handleResponse<
          ApiResponse<import('../types/google').GoogleWatch>
        >(response);
        return raw.data!;
      },
      async deleteWatch(connectionId: string, watchId: string): Promise<void> {
        const response = await fetchWithAuth(
          `${base}/conn/google/connections/${connectionId}/watches/${watchId}`,
          { method: 'DELETE' },
        );
        await handleResponse(response);
      },
    },

    gateways: {
      async list(): Promise<PaginatedResponse<Gateway>> {
        const response = await fetchWithAuth(`${base}/gateways`);
        const raw = await handleResponse<{ data: Gateway[]; pagination?: unknown }>(response);
        return { items: raw.data };
      },
      async get(gatewayId: string): Promise<Gateway> {
        const response = await fetchWithAuth(`${base}/gateways/${gatewayId}`);
        const data = await handleResponse<ApiResponse<Gateway>>(response);
        return data.data!;
      },
      async create(input: CreateGatewayInput): Promise<Gateway> {
        const response = await fetchWithAuth(`${base}/gateways`, {
          method: 'POST',
          body: JSON.stringify(input),
        });
        const data = await handleResponse<ApiResponse<Gateway>>(response);
        return data.data!;
      },
      async update(gatewayId: string, input: UpdateGatewayInput): Promise<Gateway> {
        const response = await fetchWithAuth(`${base}/gateways/${gatewayId}`, {
          method: 'PUT',
          body: JSON.stringify(input),
        });
        const data = await handleResponse<ApiResponse<Gateway>>(response);
        return data.data!;
      },
      async delete(gatewayId: string): Promise<void> {
        const response = await fetchWithAuth(`${base}/gateways/${gatewayId}`, { method: 'DELETE' });
        await handleResponse(response);
      },
      async test(gatewayId: string): Promise<GatewayTestResult> {
        const response = await fetchWithAuth(`${base}/gateways/${gatewayId}/test`, {
          method: 'POST',
        });
        const data = await handleResponse<ApiResponse<GatewayTestResult>>(response);
        return data.data!;
      },
      async previewTools(gatewayId: string): Promise<GatewayTool[]> {
        const response = await fetchWithAuth(`${base}/gateways/${gatewayId}/tools`);
        const data = await handleResponse<ApiResponse<GatewayTool[]>>(response);
        return data.data!;
      },
      async sync(gatewayId: string): Promise<GatewaySyncResult> {
        const response = await fetchWithAuth(`${base}/gateways/${gatewayId}/sync`, {
          method: 'POST',
        });
        const data = await handleResponse<ApiResponse<GatewaySyncResult>>(response);
        return data.data!;
      },
    },

    /**
     * External MCP servers registered to this space. Per-user OAuth: each
     * member connects for themselves; the agent uses its owner's connection
     * at runtime.
     */
    mcpServers: {
      async list(): Promise<McpServer[]> {
        const response = await fetchWithAuth(`${base}/mcp-servers`);
        const raw = await handleResponse<{
          data: { mcpServers?: McpServer[]; count?: number } | McpServer[];
        }>(response);
        // Mirrors the dual shape used by other list endpoints — backend may
        // wrap under `{ mcpServers, count }` or return a bare array.
        const data = raw.data;
        if (Array.isArray(data)) return data;
        return data?.mcpServers ?? [];
      },
      async get(serverId: string): Promise<McpServer> {
        const response = await fetchWithAuth(`${base}/mcp-servers/${serverId}`);
        const data = await handleResponse<ApiResponse<McpServer>>(response);
        return data.data!;
      },
      async create(input: CreateMcpServerInput): Promise<McpServer> {
        const response = await fetchWithAuth(`${base}/mcp-servers`, {
          method: 'POST',
          body: JSON.stringify(input),
        });
        const data = await handleResponse<ApiResponse<McpServer>>(response);
        return data.data!;
      },
      async update(serverId: string, input: UpdateMcpServerInput): Promise<McpServer> {
        const response = await fetchWithAuth(`${base}/mcp-servers/${serverId}`, {
          method: 'PUT',
          body: JSON.stringify(input),
        });
        const data = await handleResponse<ApiResponse<McpServer>>(response);
        return data.data!;
      },
      async delete(serverId: string): Promise<void> {
        const response = await fetchWithAuth(`${base}/mcp-servers/${serverId}`, {
          method: 'DELETE',
        });
        await handleResponse(response);
      },
      /**
       * Pull tools from the server. May return `{ authorizationRequired:true,
       * authorizationUrl }` if the caller hasn't completed consent yet.
       *
       * `returnUrl` is baked into the upstream AgentCore authorization URL —
       * where the user lands after they complete (or cancel) consent. Pass
       * `${window.location.origin}/oauth/connected` so the popup tab lands
       * on our friendly confirmation page.
       */
      async sync(serverId: string, returnUrl?: string): Promise<McpSyncResult> {
        const qs = returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : '';
        const response = await fetchWithAuth(`${base}/mcp-servers/${serverId}/sync${qs}`, {
          method: 'POST',
        });
        const data = await handleResponse<ApiResponse<McpSyncResult>>(response);
        return data.data!;
      },
      /**
       * Per-user auth status for this server. `returnUrl` (when present) is
       * baked into the returned `authorizationUrl` — same purpose as in
       * `sync` above.
       */
      async authStatus(serverId: string, returnUrl?: string): Promise<McpAuthStatus> {
        const qs = returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : '';
        const response = await fetchWithAuth(`${base}/mcp-servers/${serverId}/auth-status${qs}`);
        const data = await handleResponse<ApiResponse<McpAuthStatus>>(response);
        return data.data!;
      },
    },

    runs: {
      async list(params?: import('../types/runs').RunListParams): Promise<{
        items: RunStatusResponse[];
        nextToken?: string;
        totalCount?: number;
      }> {
        const searchParams = new URLSearchParams();
        if (params?.limit) searchParams.set('limit', String(params.limit));
        if (params?.cursor) searchParams.set('cursor', params.cursor);
        if (params?.status) searchParams.set('status', params.status);
        if (params?.agentId) searchParams.set('agentId', params.agentId);
        const qs = searchParams.toString();
        const response = await fetchWithAuth(`${base}/runs${qs ? `?${qs}` : ''}`);
        const raw = await handleResponse<{
          data: RunStatusResponse[];
          pagination: { nextToken?: string; totalCount?: number };
        }>(response);
        return {
          items: raw.data ?? [],
          nextToken: raw.pagination?.nextToken,
          totalCount: raw.pagination?.totalCount,
        };
      },
      async get(runId: string): Promise<RunStatusResponse> {
        const response = await fetchWithAuth(`${base}/runs/${runId}`);
        const raw = await handleResponse<ApiResponse<RunStatusResponse>>(response);
        return raw.data!;
      },
      async cancel(runId: string): Promise<void> {
        await fetchWithAuth(`${base}/runs/${runId}/cancel`, { method: 'POST' });
      },
      async retry(runId: string): Promise<RetryRunResponse> {
        const response = await fetchWithAuth(`${base}/runs/${runId}/retry`, { method: 'POST' });
        return handleResponse<RetryRunResponse>(response);
      },
      async artifacts(runId: string, conversationId?: string): Promise<ArtifactReference[]> {
        const params = conversationId ? `?conversationId=${encodeURIComponent(conversationId)}` : '';
        const response = await fetchWithAuth(`${base}/runs/${runId}/artifacts${params}`);
        const data = await handleResponse<ApiResponse<ArtifactReference[]>>(response);
        return data.data ?? [];
      },
      async agentTasks(runId: string): Promise<import('../types/agent-tasks').AgentTask[]> {
        const response = await fetchWithAuth(`${base}/runs/${runId}/agent-tasks`);
        const raw = await handleResponse<{
          data: { tasks: import('../types/agent-tasks').AgentTask[]; count: number };
        }>(response);
        return raw.data?.tasks ?? [];
      },
    },

    artifacts: {
      async get(artifactId: string): Promise<ArtifactReference> {
        const response = await fetchWithAuth(`${base}/artifacts/${artifactId}`);
        const data = await handleResponse<ApiResponse<ArtifactReference>>(response);
        return data.data!;
      },
    },

    schedules: {
      async list(params?: { status?: string; agentId?: string }): Promise<PaginatedResponse<Schedule>> {
        const searchParams = new URLSearchParams();
        if (params?.status) searchParams.set('status', params.status);
        if (params?.agentId) searchParams.set('agentId', params.agentId);
        const qs = searchParams.toString();
        const response = await fetchWithAuth(`${base}/schedules${qs ? `?${qs}` : ''}`);
        const raw = await handleResponse<{
          data: { schedules: Schedule[]; count: number };
        }>(response);
        return { items: raw.data?.schedules ?? [] };
      },
      async get(scheduleId: string): Promise<Schedule> {
        const response = await fetchWithAuth(`${base}/schedules/${scheduleId}`);
        const raw = await handleResponse<{
          data: { schedules: Schedule[]; count: number };
        }>(response);
        return raw.data.schedules[0];
      },
      async create(input: CreateScheduleInput): Promise<{ scheduleId: string; status: string }> {
        const response = await fetchWithAuth(`${base}/schedules`, {
          method: 'POST',
          body: JSON.stringify(input),
        });
        return handleResponse(response);
      },
      async update(
        scheduleId: string,
        input: UpdateScheduleInput,
      ): Promise<{ updated: boolean; scheduleId: string }> {
        const response = await fetchWithAuth(`${base}/schedules/${scheduleId}`, {
          method: 'PUT',
          body: JSON.stringify(input),
        });
        return handleResponse(response);
      },
      async delete(scheduleId: string): Promise<void> {
        const response = await fetchWithAuth(`${base}/schedules/${scheduleId}`, { method: 'DELETE' });
        await handleResponse(response);
      },
      async upcoming(params?: UpcomingSchedulesParams): Promise<UpcomingSchedulesResponse> {
        const searchParams = new URLSearchParams();
        if (params?.limit) searchParams.set('limit', String(params.limit));
        if (params?.filterUserId) searchParams.set('filterUserId', params.filterUserId);
        if (params?.agentId) searchParams.set('agentId', params.agentId);
        if (params?.windowDays) searchParams.set('windowDays', String(params.windowDays));
        const qs = searchParams.toString();
        const response = await fetchWithAuth(`${base}/schedules/upcoming${qs ? `?${qs}` : ''}`);
        return handleResponse<UpcomingSchedulesResponse>(response);
      },
    },

    subscriptions: {
      async list(
        params?: import('../types').ListSubscriptionsParams,
      ): Promise<PaginatedResponse<import('../types').EventSubscription>> {
        const searchParams = new URLSearchParams();
        searchParams.set('limit', '100');
        if (params?.status) searchParams.set('status', params.status);
        if (params?.subscriberId) searchParams.set('subscriberId', params.subscriberId);
        if (params?.scope) searchParams.set('scope', params.scope);
        if (params?.deliveryMode) searchParams.set('deliveryMode', params.deliveryMode);
        const response = await fetchWithAuth(`${base}/subscriptions?${searchParams}`);
        const raw = await handleResponse<{
          data: import('../types').EventSubscription[];
          pagination: unknown;
        }>(response);
        return { items: raw.data };
      },
      async update(
        subscriptionId: string,
        input: { status: import('../types').SubscriptionStatus },
      ): Promise<import('../types').EventSubscription> {
        const response = await fetchWithAuth(`${base}/subscriptions/${subscriptionId}`, {
          method: 'PATCH',
          body: JSON.stringify(input),
        });
        const raw = await handleResponse<{ data: import('../types').EventSubscription }>(response);
        return raw.data;
      },
    },

    canvases: {
      async get(
        conversationId: string,
        canvasId: string,
      ): Promise<{ canvas: Canvas; surfaces: Surface[] }> {
        const response = await fetchWithAuth(
          `${base}/conversations/${conversationId}/canvases/${canvasId}`,
        );
        const raw = await handleResponse<
          ApiResponse<{
            canvasId: string;
            title: string;
            isDefault: boolean;
            layout?: string;
            order?: number;
            createdAt?: string;
            surfaces: Array<{
              surfaceId: string;
              surfaceName?: string;
              runId?: string;
              title?: string;
              spec?: { updates?: unknown[] };
              version: number;
              order?: number;
              createdAt?: string;
              updatedAt?: string;
              uiState?: Record<string, unknown>;
            }>;
          }>
        >(response);
        const cv = raw.data!;
        const canvas: Canvas = {
          canvasId: cv.canvasId,
          conversationId,
          title: cv.title,
          isDefault: cv.isDefault,
          layout: (cv.layout as CanvasLayout) ?? 'stack',
          ...(cv.order !== undefined ? { order: cv.order } : {}),
          createdAt: cv.createdAt ?? new Date().toISOString(),
        };
        const surfaces: Surface[] = (cv.surfaces ?? []).map((s) => ({
          surfaceId: s.surfaceId,
          ...(s.surfaceName ? { surfaceName: s.surfaceName } : {}),
          canvasId: cv.canvasId,
          runId: s.runId,
          title: s.title ?? '',
          version: s.version,
          spec: { updates: Array.isArray(s.spec?.updates) ? (s.spec.updates as AuiNode[]) : [] },
          ...(s.order !== undefined ? { order: s.order } : {}),
          createdAt: s.createdAt,
          updatedAt: s.updatedAt,
          ...(s.uiState ? { uiState: s.uiState } : {}),
        }));
        return { canvas, surfaces };
      },

      async getSurface(canvasId: string, surfaceId: string): Promise<Surface> {
        const response = await fetchWithAuth(
          `${base}/canvases/${canvasId}/surfaces/${surfaceId}`,
        );
        const raw = await handleResponse<ApiResponse<Surface> | Surface>(response);
        const surface = ((raw as ApiResponse<Surface>).data ?? raw) as Surface;
        return {
          ...surface,
          spec: { updates: Array.isArray(surface.spec?.updates) ? surface.spec.updates : [] },
        };
      },

      async deleteSurface(canvasId: string, surfaceId: string): Promise<void> {
        const response = await fetchWithAuth(
          `${base}/canvases/${canvasId}/surfaces/${surfaceId}`,
          { method: 'DELETE' },
        );
        await handleResponse(response);
      },

      async deleteCanvas(conversationId: string, canvasId: string): Promise<void> {
        const response = await fetchWithAuth(
          `${base}/conversations/${conversationId}/canvases/${canvasId}`,
          { method: 'DELETE' },
        );
        await handleResponse(response);
      },

      async update(
        conversationId: string,
        canvasId: string,
        patch: { title?: string },
      ): Promise<Canvas> {
        const response = await fetchWithAuth(
          `${base}/conversations/${conversationId}/canvases/${canvasId}`,
          { method: 'PATCH', body: JSON.stringify(patch) },
        );
        const raw = await handleResponse<ApiResponse<Canvas>>(response);
        return raw.data!;
      },

      async reorderCanvases(
        conversationId: string,
        canvasIds: string[],
      ): Promise<{ canvases: Canvas[] }> {
        const response = await fetchWithAuth(
          `${base}/conversations/${conversationId}/canvases/order`,
          { method: 'PATCH', body: JSON.stringify({ canvasIds }) },
        );
        const raw = await handleResponse<ApiResponse<{ canvases: Canvas[] }>>(response);
        return raw.data!;
      },

      async moveSurfaces(
        fromCanvasId: string,
        body: MoveSurfacesInput,
      ): Promise<MoveSurfacesResponse> {
        const response = await fetchWithAuth(
          `${base}/canvases/${fromCanvasId}/surfaces/move`,
          { method: 'POST', body: JSON.stringify(body) },
        );
        const raw = await handleResponse<ApiResponse<MoveSurfacesResponse>>(response);
        return raw.data!;
      },

      async reorderSurfaces(
        canvasId: string,
        surfaceIds: string[],
      ): Promise<{ surfaces: Surface[] }> {
        const response = await fetchWithAuth(
          `${base}/canvases/${canvasId}/surfaces/order`,
          { method: 'PATCH', body: JSON.stringify({ surfaceIds }) },
        );
        const raw = await handleResponse<ApiResponse<{ surfaces: Surface[] }>>(response);
        return raw.data!;
      },

      surfaceUiStateUrl(canvasId: string, surfaceId: string): string {
        return `${base}/canvases/${canvasId}/surfaces/${surfaceId}/ui-state`;
      },

      async updateSurfaceUiState(
        canvasId: string,
        surfaceId: string,
        uiState: Record<string, unknown>,
      ): Promise<{
        surfaceId: string;
        version: number;
        uiState: Record<string, unknown>;
        updatedAt: string;
      }> {
        const body = JSON.stringify({ uiState });
        if (body.length > 30_000) {
          const err = new Error('uiState payload too large') as Error & { code?: string };
          err.code = 'PAYLOAD_TOO_LARGE';
          throw err;
        }
        const response = await fetchWithAuth(
          `${base}/canvases/${canvasId}/surfaces/${surfaceId}/ui-state`,
          { method: 'PATCH', body },
        );
        const raw = await handleResponse<
          ApiResponse<{
            surfaceId: string;
            version: number;
            uiState: Record<string, unknown>;
            updatedAt: string;
          }>
        >(response);
        return raw.data!;
      },
    },

    delegatedTasks: {
      async list(conversationId: string): Promise<import('../types/delegated-tasks').DelegatedTask[]> {
        const response = await fetchWithAuth(
          `${base}/conversations/${conversationId}/delegated-tasks`,
        );
        const raw = await handleResponse<{
          data: { tasks: Record<string, unknown>[]; count: number };
        }>(response);
        const apiTasks = raw.data?.tasks ?? [];
        return apiTasks.map((t) => ({
          taskId: t.taskId as string,
          operatorRunId: t.operatorRunId as string,
          operatorConversationId: t.operatorConversationId as string,
          targetAgentId: (t.agentId as string) ?? '',
          targetAgentName: t.agentName as string | undefined,
          delegatedRunId: t.rootRunId as string | undefined,
          targetCanvasId: t.canvasId as string | undefined,
          title: t.title as string | undefined,
          prompt: (t.prompt as string) ?? '',
          status: t.status as import('../types/delegated-tasks').DelegatedTaskStatus,
          response: t.response as string | undefined,
          error: t.error as string | undefined,
          durationMs: t.durationMs as number | undefined,
          createdAt: t.createdAt as string,
          updatedAt: t.updatedAt as string,
          summary: t.summary as import('../types/delegated-tasks').DelegatedTaskSummary | undefined,
          referencedSurfaces: Array.isArray(t.referencedSurfaces)
            ? (t.referencedSurfaces as import('../types').ResolvedSurfaceRef[])
            : undefined,
        }));
      },
      async dismiss(
        conversationId: string,
        taskId: string,
      ): Promise<{
        success: boolean;
        taskId: string;
        messageId: string | null;
        dismissed: boolean;
      }> {
        const response = await fetchWithAuth(
          `${base}/conversations/${conversationId}/delegated-tasks/${taskId}`,
          { method: 'DELETE' },
        );
        return handleResponse(response);
      },
      async rerun(
        conversationId: string,
        taskId: string,
      ): Promise<{
        success: boolean;
        originalTaskId: string;
        count: number;
        tasks: Array<{
          taskId: string;
          runId: string;
          conversationId: string;
          canvasId: string;
          agentId: string;
          agentName: string;
          title: string;
          status: string;
        }>;
      }> {
        const response = await fetchWithAuth(
          `${base}/conversations/${conversationId}/delegated-tasks/${taskId}/rerun`,
          { method: 'POST' },
        );
        return handleResponse(response);
      },
    },
  };
}

/**
 * Org-scoped API factory — mirrors `spaceApi(spaceId)` but for resources owned
 * by an organization rather than a space. The MCP-servers namespace covers
 * org-level installs that auto-attach to every space (admin-only).
 *
 * Hooks typically wrap this in `useMemo(() => orgApi(orgId), [orgId])` so the
 * returned object identity is stable per org.
 */
export function orgApi(orgId: string) {
  const base = `${API_BASE}/api/v1/orgs/${orgId}`;

  return {
    orgId,

    mcpServers: {
      async list(): Promise<OrgMcpServer[]> {
        const response = await fetchWithAuth(`${base}/mcp-servers`);
        const raw = await handleResponse<{
          data: { mcpServers?: OrgMcpServer[]; count?: number } | OrgMcpServer[];
        }>(response);
        const data = raw.data;
        if (Array.isArray(data)) return data;
        return data?.mcpServers ?? [];
      },
      async get(serverId: string): Promise<OrgMcpServer> {
        const response = await fetchWithAuth(`${base}/mcp-servers/${serverId}`);
        const data = await handleResponse<ApiResponse<OrgMcpServer>>(response);
        return data.data!;
      },
      async create(input: CreateOrgMcpServerInput): Promise<OrgMcpServer> {
        const response = await fetchWithAuth(`${base}/mcp-servers`, {
          method: 'POST',
          body: JSON.stringify(input),
        });
        const data = await handleResponse<ApiResponse<OrgMcpServer>>(response);
        return data.data!;
      },
      async update(
        serverId: string,
        input: UpdateOrgMcpServerInput,
      ): Promise<OrgMcpServer> {
        const response = await fetchWithAuth(`${base}/mcp-servers/${serverId}`, {
          method: 'PUT',
          body: JSON.stringify(input),
        });
        const data = await handleResponse<ApiResponse<OrgMcpServer>>(response);
        return data.data!;
      },
      async delete(serverId: string): Promise<void> {
        const response = await fetchWithAuth(`${base}/mcp-servers/${serverId}`, {
          method: 'DELETE',
        });
        await handleResponse(response);
      },
      /** Pull tools from the org install. Same authorizationRequired pattern
       *  as the space-scoped variant — see {@link spaceApi}'s mcpServers.sync. */
      async sync(serverId: string, returnUrl?: string): Promise<McpSyncResult> {
        const qs = returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : '';
        const response = await fetchWithAuth(
          `${base}/mcp-servers/${serverId}/sync${qs}`,
          { method: 'POST' },
        );
        const data = await handleResponse<ApiResponse<McpSyncResult>>(response);
        return data.data!;
      },
      /** Per-user auth status against the org install (Tier 2 in the fallback
       *  chain: space → personal → org-SA). */
      async authStatus(
        serverId: string,
        returnUrl?: string,
      ): Promise<McpAuthStatus> {
        const qs = returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : '';
        const response = await fetchWithAuth(
          `${base}/mcp-servers/${serverId}/auth-status${qs}`,
        );
        const data = await handleResponse<ApiResponse<McpAuthStatus>>(response);
        return data.data!;
      },
      /** Store the service-account credential. PUT-only: backend has no
       *  delete in v1 so the UI must not expose a "clear" affordance. */
      async putServiceAccountCredential(
        serverId: string,
        credential: OrgServiceAccountCredential,
      ): Promise<OrgMcpServer> {
        const response = await fetchWithAuth(
          `${base}/mcp-servers/${serverId}/service-account-credential`,
          { method: 'PUT', body: JSON.stringify(credential) },
        );
        const data = await handleResponse<ApiResponse<OrgMcpServer>>(response);
        return data.data!;
      },
    },

    /**
     * Admin-curated marketplace repos + their cached, browsable plugin entries.
     * `getSources` / `list` are readable by any org member; `putSources` and
     * `sync` 403 for non-admins. Each method runs through a thin adapter so the
     * spec's still-evolving wrapper field names normalize in one place.
     *
     * Note: these endpoints live under `/organizations/{orgId}/...`, NOT the
     * `/orgs/{orgId}/...` base the rest of this namespace uses. The admin-api
     * stack spells the segment out.
     */
    pluginCatalog: (() => {
      const catalogBase = `${API_BASE}/api/v1/organizations/${orgId}`;
      return {
      async getSources(): Promise<OrgPluginCatalogSourcesResponse> {
        const response = await fetchWithAuth(`${catalogBase}/plugin-catalog/sources`);
        const raw = await handleResponse<
          | OrgPluginCatalogSourcesResponse
          | ApiResponse<OrgPluginCatalogSourcesResponse>
          | { sources?: OrgPluginCatalogSource[] }
        >(response);
        const unwrapped =
          (raw as ApiResponse<OrgPluginCatalogSourcesResponse>).data ?? raw;
        const sources =
          (unwrapped as { sources?: OrgPluginCatalogSource[] }).sources ?? [];
        return { sources };
      },
      async putSources(
        sources: OrgPluginCatalogSource[],
      ): Promise<OrgPluginCatalogSourcesResponse> {
        const response = await fetchWithAuth(`${catalogBase}/plugin-catalog/sources`, {
          method: 'PUT',
          body: JSON.stringify({ sources }),
        });
        const raw = await handleResponse<
          | OrgPluginCatalogSourcesResponse
          | ApiResponse<OrgPluginCatalogSourcesResponse>
          | { sources?: OrgPluginCatalogSource[] }
        >(response);
        const unwrapped =
          (raw as ApiResponse<OrgPluginCatalogSourcesResponse>).data ?? raw;
        return {
          sources:
            (unwrapped as { sources?: OrgPluginCatalogSource[] }).sources ?? [],
        };
      },
      /** 202 ack; the cache populates asynchronously. Caller polls GET /plugin-catalog. */
      async sync(): Promise<{ status: 'syncing' }> {
        const response = await fetchWithAuth(`${catalogBase}/plugin-catalog/sync`, {
          method: 'POST',
        });
        await handleResponse(response);
        return { status: 'syncing' };
      },
      async list(): Promise<OrgPluginCatalogResponse> {
        const response = await fetchWithAuth(`${catalogBase}/plugin-catalog`);
        const raw = await handleResponse<
          | OrgPluginCatalogResponse
          | ApiResponse<OrgPluginCatalogResponse>
          | {
              plugins?: OrgPluginCatalogEntry[];
              entries?: OrgPluginCatalogEntry[];
              count?: number;
              customPluginsAllowed?: boolean;
            }
        >(response);
        const unwrapped =
          (raw as ApiResponse<OrgPluginCatalogResponse>).data ?? raw;
        const body = unwrapped as {
          plugins?: OrgPluginCatalogEntry[];
          entries?: OrgPluginCatalogEntry[];
          count?: number;
          customPluginsAllowed?: boolean;
        };
        const plugins = body.plugins ?? body.entries ?? [];
        return {
          plugins,
          count: body.count ?? plugins.length,
          customPluginsAllowed: body.customPluginsAllowed ?? true,
        };
      },
      };
    })(),

    /**
     * Admin-only usage analytics (dot plot + summary + catalog), served by the
     * `{env}-optr-analytics` stack under `/organizations/{orgId}/...` (spelled
     * out, like the plugin catalog above — NOT the `/orgs/{orgId}` base).
     *
     * `dotplot` and `summary` compute asynchronously: a 202 carries a
     * `{status:'pending'}` body and a 200 carries `{status:'ready', meta, …}`.
     * Both are `response.ok`, so `handleResponse` returns the body verbatim and
     * the caller branches on `status` — the hooks poll while pending. Responses
     * are flat (not `{data}`-wrapped), so no unwrapping here.
     */
    analytics: (() => {
      const analyticsBase = `${API_BASE}/api/v1/organizations/${orgId}/analytics`;
      const qs = (params: Record<string, string | number | boolean | undefined>) => {
        const sp = new URLSearchParams();
        for (const [k, v] of Object.entries(params)) {
          if (v !== undefined && v !== '') sp.set(k, String(v));
        }
        const s = sp.toString();
        return s ? `?${s}` : '';
      };
      return {
        async getDotplot(opts: {
          days?: number;
          limit?: number;
          refresh?: boolean;
        } = {}): Promise<DotplotResponse> {
          const response = await fetchWithAuth(
            `${analyticsBase}/dotplot${qs({
              days: opts.days,
              limit: opts.limit,
              refresh: opts.refresh ? true : undefined,
            })}`,
          );
          return handleResponse<DotplotResponse>(response);
        },
        async getSummary(opts: {
          days?: number;
          refresh?: boolean;
        } = {}): Promise<SummaryResponse> {
          const response = await fetchWithAuth(
            `${analyticsBase}/summary${qs({
              days: opts.days,
              refresh: opts.refresh ? true : undefined,
            })}`,
          );
          return handleResponse<SummaryResponse>(response);
        },
        async getCatalog(): Promise<CatalogResponse> {
          const response = await fetchWithAuth(`${analyticsBase}/catalog`);
          return handleResponse<CatalogResponse>(response);
        },
      };
    })(),
  };
}

export { ApiError };
